import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { api } from './api'
import { Abrufausfall, useAbruf, useHintergrundabruf } from './Abruf'
import { useListenwahl } from './Listenwahl'
import { Auswahlfeld, ausStammdatum } from './Auswahlfeld'
import { fachlicherSatz } from './Fehlersatz'
import { meldeErfolg } from './Erfolgsstelle'
import { Dialogfenster } from './Dialogfenster'
import {
  aktuelleSprache,
  anwendername,
  anzahlwort,
  beschriftung,
  feldname,
  gebietsschema,
  gesellschaftsname,
  katalogbezeichnung,
  liesAnteil,
  liesDezimal,
  mitNamen,
  ohneVortrag,
  periodenname,
  speicherform,
  t,
  textOderRoh,
  vorschlagssatz,
  zahlenschema,
  zahlenwort,
} from './sprache'
import { Periodenfeld } from './Periodenfeld'
import { useDarf } from './Rechte'
import { Zahlenfeld } from './Zahlenfeld'
import { Kontofeld } from './Kontofeld'
// **Dasselbe Bauteil wie in der Positionsgliederung** (R700): Wer eine
// Gliederung baut, benutzt die Bauteile der Gliederungen und keine eigenen.
import { Stammdatentafel } from './Stammdatentafel'
import { Zeilenmenue } from './Zeilenmenue'
import { Reihenfolgefenster } from './Reihenfolgefenster'
import type { Reihenfolgeziel } from './Reihenfolgefenster'
import { ausPeriodenfeld } from './Zeitraum'
import type { Sprachschluessel } from './sprache'
import { Spaltenmassleiste, useSpaltenmasse } from './Spaltenbreiten'
import type {
  Beschriftung,
  Fassungsangabe,
  Haussprache,
  Feldangabe,
  Listeneintrag,
  Objektartbeschreibung,
  Pflegeauftrag,
  Sprachzeile,
  Stammdatenblatt,
  Verwendung,
  Loeschlage,
  Loeschauftrag,
  Zahlendarstellung,
} from './typen'

/*
  **Ersatzwerte der Abrufe im Hintergrund** (7.2 Abschnitt 39, R1212): Was
  gilt, solange ein Nachschlagen unterwegs ist oder wenn es nicht kam — keine
  Wahl, keine Namen. Auf Modulebene, damit der Ersatz bei jeder Darstellung
  derselbe Wert ist.
*/
const KEINE_SPRACHEN: Haussprache[] = []
const KEIN_VORRAT: Record<string, Listeneintrag[]> = {}
const KEINE_NAMEN: Record<string, Beschriftung> = {}
const KEINE_ZIELARTEN: Record<string, string> = {}
const KEINE_EINTRAEGE: Listeneintrag[] = []
/** Solange die Beschreibung der Objektarten unterwegs ist oder nicht kam. */
const KEINE_ARTEN: Objektartbeschreibung[] = []

/**
 * Welcher Unterpunkt welche Objektarten pflegt (7.4, R235 · 7.2, R156).
 *
 * **Der Unterpunkt ist die Adresse, die Objektart eine Auswahl darin.**
 * *Kontenrahmen* zeigt Konten **und** Kontengruppen: Die Gruppe ist der
 * Träger der Kontoeigenschaften (0019), und wer ein geerbtes Feld verstehen
 * will, muss beides nebeneinander haben. Dasselbe bei *Positionspläne* —
 * ein Plan ohne seine Positionen ist eine leere Hülle.
 *
 * **Die Kreise stehen in der Konzernstruktur, nicht hier** (7.2): Ein Ort,
 * zwei Wege ist erlaubt; zwei Orte für dieselbe Sache nicht.
 *
 * *Währungskurse* und *Abstimmkreise* fehlen mit Absicht. Kurse sind eine
 * Tabelle mit Zeitachse und kein Blatt mit Eigenschaften — sie bekommen eine
 * eigene Maske (7.4, Abschnitt 9); Abstimmkreise gibt es im Modell noch nicht.
 */
/**
 * Welche Felder eine Übersichtstafel zeigt — **wo weniger die Antwort ist**
 * (R412, Auftrag vom 2026-09-26).
 *
 * **Der Befund:** Die Tafel der Konsolidierungskreise trug neun Spalten, und
 * vier davon waren leer. *Vorausgewählter Positionsplan*, *GuV-Umrechnung*
 * und *Fonds mit Kontokorrent* haben eine Bedeutung — nur beantwortet keine
 * davon eine Frage, die man auf einer **Übersicht** stellt. Der Auftraggeber:
 * *„Einige Felder machen für mich keinen erkennbaren Sinn."*
 *
 * **Alles Übrige steht im Blatt** und ist einen Klick entfernt. Die Tafel
 * beantwortet die Frage *welchen Kreis meine ich?*, das Blatt die Frage *wie
 * ist er eingestellt?*
 *
 * **Fehlt eine Objektart hier, zeigt die Tafel alle Felder** — das ist der
 * Normalfall und bleibt es. Eingetragen wird nur, wo jemand gezählt und
 * gestrichen hat.
 */
export const TAFELSPALTEN: Record<string, string[]> = {
  konsolidierungskreis: ['oberste_einheit', 'uebergeordneter_kreis', 'konzernwaehrung'],
}

export const STAMMDATENARTEN: Record<string, string[]> = {
  'stammdaten/einheiten': ['einheit'],
  'stammdaten/kontenrahmen': ['konto', 'kontengruppe'],
  // Ohne Positionsgliederung: Die pflegt der Baum darüber (R1453).
  'stammdaten/positionsplaene': ['positionsplan', 'position', 'kontozuordnung'],
  'stammdaten/ledger': ['ledger'],
  'stammdaten/bewegungsarten': ['bewegungsart'],
  'stammdaten/analysedimensionen': ['analysedimension', 'dimensionswert', 'segment'],
  'stammdaten/partnercodes': ['partnercode'],
  // **Der Vorrat der Währungen** (7.6 Abschnitt 12, R681): Erst die Währung,
  // dann die Kurstabelle, die zwei davon verbindet.
  'stammdaten/waehrungen': ['waehrung'],
  // **Der Vorrat der Länder** (7.4 Abschnitt 22, R692): dieselbe Bauform wie
  // die Währungen und aus demselben Grund — der Code der Norm ist der
  // Schlüssel, und die Bezeichnung steht je Sprache daneben.
  'stammdaten/laender': ['land'],
  // **Der Feldkatalog** (R554): Das Blatt, auf dem die Blätter der anderen
  // entstehen. Es ist selbst eines von ihnen — dieselbe Liste, dieselbe
  // Fassungsreihe, dieselbe Stilllegung (R317).
  'stammdaten/eigenschaften': ['feld'],
  // **Der Ordnerbaum** (R558): der Ordner und die Zuordnung der Gesellschaft
  // an einem Ort — wie Konto und Kontengruppe im Kontenrahmen, weil das eine
  // ohne das andere nicht zu verstehen ist.
  'stammdaten/ordner': ['organisationsordner', 'organisationszuordnung'],
  // **Woran ein Bericht hängt** (2.3 Abschnitt 10, R712): Kreise,
  // Gesellschaften, Ordner und eigene Dimensionswerte stehen hier
  // nebeneinander — nicht weil sie verwandt wären, sondern weil sie dieselbe
  // Frage beantworten: Welchen Ausschnitt sehe ich oben im Bericht?
  'stammdaten/organisationselemente': ['organisationselement'],
  // **Die Konzernstruktur ist pflegbar** (seit 2026-09-14): Beteiligung und
  // Erstkonsolidierung im Register, die Kreiszugehörigkeit bei den Kreisen.
  // Ohne diese drei ließ sich eine neue Gesellschaft anlegen — und an
  // niemanden hängen (2.2 Abschnitt 3.2, 2.3 Abschnitt 3.2).
  'konzernstruktur/beteiligungsregister': ['beteiligung', 'erstkonsolidierung', 'kapitaltransaktion'],
  // Ohne Kreiszugehörigkeit: Die pflegt das Blatt des Kreises (R1453).
  'konzernstruktur/kreise': ['konsolidierungskreis'],
}

/**
 * Womit eine Objektart aufgeschlagen wird — **wo ihre Gliederung fachlich ist
 * und nicht eine Arbeitssicht unter vielen** (R708).
 *
 * **Die Bewegungsart ist bisher die einzige.** Ihre Kategorie *ist* der
 * Spiegel (R688), und die Reihenfolge darunter *ist* die Reihenfolge der
 * Spalten (R706) — beides sind Eigenschaften des Stammdatums und keine
 * Vorliebe des Betrachters. Ein Konto dagegen hat keine solche Ordnung: Wer es
 * nach Kontengruppe gliedert, tut das für eine bestimmte Arbeit.
 *
 * **Sie ist nur die Vorgabe und keine Fessel** (R557): Wer im Menü darüber
 * etwas anderes wählt, bekommt es — und findet es morgen wieder, weil die
 * Sicht gemerkt wird. Ohne diese Vorgabe stand die Gliederung als eine von
 * neun im Menü und war beim Aufschlagen nicht da.
 */
const GLIEDERUNG_AB_WERK: Record<string, { gruppe: string; sortierung: string }> = {
  bewegungsart: { gruppe: 'spiegel', sortierung: 'sortierung' },
}

/**
 * **Wessen Reihenfolge das Fenster per Rechtsklick ändert** (7.4 Abschnitt 32,
 * R1341) — die Objektarten der Liste mit eigener Reihenfolge. Partnercodes
 * und Gliederung haben ihren eigenen Baum und dort denselben Menüpunkt.
 */
const REIHENFOLGEARTEN = new Set(['bewegungsart', 'organisationselement', 'organisationsordner'])

/**
 * Die Gruppe, die zuoberst steht, weil sie für alle anderen gilt.
 *
 * **Startwert und Buchwert der Periode rahmen jeden Spiegel** (R689) und
 * gehören deshalb keiner Kategorie, sondern allen — im Modell über
 * `spiegel = alle`. In der Liste stünden sie sonst alphabetisch zwischen
 * *Anlagenspiegel* und *Eigenkapitalspiegel*, als wären sie eine Kategorie
 * neben ihnen.
 *
 * **Fünfmal dieselbe Zeile wäre die schlechtere Antwort:** Wer eine davon
 * anklickt, bearbeitet dasselbe Stammdatum — fünf Einträge, die zu einem
 * führen, sind vier Einträge zu viel.
 */
const GRUPPE_ZUOBERST: Record<string, string> = { spiegel: 'alle' }

/**
 * Die Gruppe, die zuletzt steht, weil sie keine ist.
 *
 * **„Kein Spiegel" ist keine Kategorie, sondern ihre Abwesenheit** — dieselbe
 * Aussage wie *Ohne Angabe*, nur ausdrücklich gepflegt. Alphabetisch stand sie
 * vor *Kreditspiegel*, und damit lag die Restgruppe mitten in der Gliederung.
 */
const GRUPPE_ZULETZT: Record<string, string> = { spiegel: 'keiner' }

/**
 * Was im leeren Blatt steht, wo *Nichts ausgewählt* zu wenig sagt.
 *
 * **Nur wo der Bereich selbst erklärungsbedürftig ist.** Bei den Konten weiß
 * jeder, was links steht. Bei den optionalen Dimensionen ist die eigentliche
 * Auskunft, dass es sie überhaupt gibt und dass der Anwender sie selbst
 * einrichtet — 7.1 verbietet den leeren Bildschirm, und „Nichts ausgewählt"
 * ist hier genau das: eine Aussage über den Klick, nicht über den Bereich.
 *
 * Der Schlüssel ist die **erste** Objektart des Bereichs, weil das die ist,
 * die beim Aufschlagen gewählt ist.
 */
const LEERHINWEIS: Record<string, { titel: Sprachschluessel; text: Sprachschluessel }> = {
  analysedimension: {
    titel: 'stammdaten.leer.dimensionen.titel',
    text: 'stammdaten.leer.dimensionen.text',
  },
  // **Derselbe Fall** (R712): Dass es diesen Vorrat gibt und dass er über den
  // Berichten steht, ist die Auskunft — nicht, dass gerade nichts angeklickt
  // ist.
  organisationselement: {
    titel: 'stammdaten.leer.organisationselemente.titel',
    text: 'stammdaten.leer.organisationselemente.text',
  },
}

/**
 * Der Bereich *Stammdaten* — ein Blatt aus Eigenschaften für jede Objektart
 * (7.4, R235–R246).
 *
 * **Ein Bauteil für acht Objektarten.** Eine Gesellschaft, ein Konto und eine
 * Bewegungsart haben verschiedene Felder und dieselbe Gestalt: eine Kennung,
 * die sich nie ändert; einen Code, den der Anwender liest; Beschriftungen je
 * Sprache; eine Handvoll Fachfelder; eine Fassungsreihe darunter. Acht Masken
 * für denselben Aufbau wären achtmal dieselbe Entscheidung — und beim neunten
 * Objekt wieder eine neue.
 *
 * **Die Oberfläche kennt die Objektarten nicht.** Welche Felder es gibt,
 * welchen Wertevorrat sie haben und was vererbt wird, sagt
 * `GET /api/stammdaten/arten`. Stünde es hier noch einmal, wäre jedes neunte
 * Objekt eine Änderung an zwei Stellen — und eine davon würde vergessen.
 *
 * **Dreispaltig wie der Anhang** (2.15), aus demselben Grund: links wählen, in
 * der Mitte arbeiten, rechts nachschlagen.
 */
export function Stammdaten({
  zulaessig,
  vorauswahl,
  aufAenderung,
  zusatz,
  zeilenzusatz,
  tafel,
  ohneAnlegen,
  nurBlatt,
  ohneEigenschaften,
  mitNummer,
  ohneAussondern,
}: {
  zulaessig: string[]
  /**
   * Nur das Blatt, ohne die Liste daneben — **das Eigenschaften-Fenster**
   * (R857, 2.8 Abschnitt 13.22, zweiter Nachtrag).
   *
   * **Dasselbe Blatt, nicht ein zweites.** Wer eine Zeile dort pflegt, wo sie
   * steht, braucht keine Liste, aus der er sie erst heraussucht — aber
   * dieselben Felder, Zwecksätze und Fassungen wie hier. Ein eigenes Bauteil
   * für dieselbe Aufgabe liefe mit der Zeit auseinander.
   */
  nurBlatt?: boolean
  /**
   * Das Blatt **ohne den Block Eigenschaften** — nur Bezeichnungen und Kürzel
   * (R1328). Für die Zeile der Kapitalflussrechnung: Ihre Eigenschaften leitet
   * CoKo aus der Stelle in der Struktur ab, und was im Fenster zählt, ist,
   * was sie zieht (2.8 Abschnitt 13.39).
   */
  ohneEigenschaften?: boolean
  /**
   * **Die Nummer als Feld** über den Bezeichnungen (2.8 Abschnitt 13.41,
   * R1336). Sonst steht sie nur in der Überschrift: Bei den meisten
   * Objektarten vergibt das System sie (R676).
   */
  mitNummer?: boolean
  /** Ohne den Knopf *Aussondern* — wo ein anderer Weg aufräumt (R1334). */
  ohneAussondern?: boolean
  /**
   * Die Objektarten, die das Blatt **nicht** selbst anlegt (R701).
   *
   * **Für die Bereiche, in denen ein Baum darüber steht.** Ein Partnercode
   * entsteht an der Stelle, an der er stehen soll — unter einer Gruppe, in
   * einer Reihenfolge; beides weiß nur der Baum. Derselbe Knopf hier legte
   * ihn stattdessen stumm auf die oberste Ebene, und der Anwender hatte zwei
   * Knöpfe nebeneinander, die verschiedene Dinge taten und gleich hießen.
   *
   * **Eine Liste und kein Ja/Nein**, weil ein Unterpunkt mehrere Objektarten
   * führt: Unter *Positionspläne* stehen Plan, Position, Freigabe und
   * Kontozuordnung nebeneinander, und der Baum darüber legt nur **Positionen**
   * an. Ein Knopf, der für den ganzen Bereich verschwände, nähme dem Anwender
   * die einzige Stelle, an der ein neuer Positionsplan entsteht.
   *
   * **Gepflegt wird weiter hier**: Bezeichnungen, Eigenschaften, Fassungen —
   * alles, was an einer einzelnen Zeile hängt.
   */
  ohneAnlegen?: string[]
  /**
   * Ob über der Liste der **ganze Bereich als geschlossene Tabelle** steht
   * (7.4 Abschnitt 20, Auftrag vom 2026-09-19).
   *
   * **Wo, sagt `TAFELBEREICHE` in `App.tsx`** — nicht diese Liste hier und
   * nicht das Bauteil selbst: Es ist eine Entscheidung über einen Bildschirm,
   * und die steht bei den Adressen.
   */
  tafel?: boolean
  /**
   * Ein Element, das beim Aufschlagen schon offen sein soll (R317).
   *
   * **Für die Bereiche, die eine eigene Übersicht davor haben** — im
   * Datenimport steht über der Pflege eine Tafel, die sagt, welche Quelle
   * heute etwas hergibt (R297). Wer dort eine Zeile anklickt, erwartet sie
   * unten aufgeschlagen und nicht eine zweite Suche.
   */
  vorauswahl?: { objektart: string; schluessel: string } | null
  /**
   * Nach jedem Schreiben — für die Übersichten, die davor stehen.
   *
   * Die Bereitschaftstafel im Datenimport sagt, ob eine Quelle heute etwas
   * hergibt; wer sie hier stilllegt, soll das oben nicht weiter als „läuft"
   * lesen.
   */
  aufAenderung?: () => void
  /**
   * Abschnitte, die ein Arbeitsbereich dem Blatt hinzufügt — die Datei und
   * die Zuordnung einer Datenquelle (R526). Sie stehen unter den
   * Eigenschaften und nur im heutigen Blick: In einer Vergangenheit wird
   * nicht gepflegt (R517).
   */
  zusatz?: (blatt: Stammdatenblatt) => React.ReactNode
  /** Was eine Zeile der Liste zusätzlich sagt — ob eine Quelle läuft und was offen ist (R297, R523). */
  zeilenzusatz?: (eintrag: Listeneintrag) => React.ReactNode
}) {
  /*
    **Die Beschreibung der Objektarten trägt Tafel, Artenwahl und Blatt — ein
    Hauptabruf** (7.2 Abschnitt 39, R1211). Bis zum 2026-10-06 lief sie still
    im Hintergrund (R1007), und ein Ausfall ließ Tafel und Artenwahl wortlos
    weg; seither steht „Ließ sich nicht laden" darüber (R1210).
  */
  const artenabruf = useAbruf(
    () =>
      api
        .stammdatenarten()
        // In der Reihenfolge des Unterpunkts, nicht der API: Im Kontenrahmen
        // steht das Konto vorn und die Gruppe dahinter — man kommt wegen der
        // Konten und schlägt die Gruppe nach, nicht umgekehrt.
        .then((alle) =>
          alle
            .filter((a) => zulaessig.includes(a.objektart))
            .sort((a, b) => zulaessig.indexOf(a.objektart) - zulaessig.indexOf(b.objektart)),
        ),
    [zulaessig],
  )
  const arten = artenabruf.wert ?? KEINE_ARTEN
  // **Was das Haus an Sprachen erklaert hat** (R556). Einmal je Bildschirm
  // geholt: Die Liste aendert sich selten und wird an jedem Blatt gebraucht.
  //
  // **Leer heisst: keine zweite Sprache anbieten**, nicht: freies Feld.
  // Ein Ausfall des Registers darf nicht dazu fuehren, dass jemand wieder
  // eine Sprache erfindet (R556). Eine Auswahlliste — im Hintergrund, Ersatz:
  // keine zweite Sprache (R1211, R1212).
  const sprachen = useHintergrundabruf(() => api.sprachregister(), [], KEINE_SPRACHEN)
  const [objektart, setObjektart] = useState(zulaessig[0] ?? 'einheit')
  // **Die Liste ist der Inhalt der linken Spalte — ein Hauptabruf** (7.2
  // Abschnitt 39, R1211). Je Objektart frisch: Die Liste der vorigen Art
  // gehört nicht unter die neue.
  const listeabruf = useAbruf(() => api.stammdatenliste(objektart), [objektart], true)
  const liste = listeabruf.wert
  const setListe = listeabruf.setze
  // **Die Auswahl gehört zur Objektart** (7.2 Abschnitt 48, R1504): Wechselt
  // die Art, ist sie im selben Augenblick leer. Bis zum 2026-10-09 leerte sie
  // erst ein Haken nach dem Wechsel, und dazwischen fragte das Blatt nach
  // `bewegungsart/KTO-0210` — das Störungsfenster.
  const [gewaehlt, setGewaehlt] = useListenwahl<string | null>(objektart, null)
  const [suche, setSuche] = useState('')

  /**
   * Wie breit die linke Spalte steht — **gezogen, nicht fest** (Auftrag vom
   * 2026-09-27).
   *
   * **Sie stand auf höchstens 300 Bildpunkten**, und darin blieb von *„100
   * Zugang zu Anschaffungskosten"* ein *„100 Zugang zu Ansc…"* übrig. Die
   * Vorgabe ist jetzt breiter, und wer mehr will, zieht.
   *
   * **Gemerkt wie jede andere Sicht** (R557): im Browser dieses Anwenders,
   * ohne jede Wirkung auf das Stammdatum. Die Grenzen hält das Stilblatt —
   * eine Spalte, die schmaler wird als ihre Gruppenköpfe, oder breiter als
   * das Blatt daneben, hilft niemandem.
   */
  const [spaltenbreite, setSpaltenbreite] = useState(
    () => Number(gemerkteSicht(SICHTWEIT, 'spaltenbreite', String(SPALTENVORGABE))) || SPALTENVORGABE,
  )

  function setzeBreite(wunsch: number) {
    const breite = Math.min(640, Math.max(220, Math.round(wunsch)))
    setSpaltenbreite(breite)
    merkeSicht(SICHTWEIT, 'spaltenbreite', String(breite))
  }
  // **Gruppierung und Sortierung sind die Sicht eines Anwenders** (R557):
  // gemerkt, nicht gespeichert, und ohne jede Wirkung auf das Stammdatum.
  // Gemerkt heißt: im Browser dieses Anwenders, je Objektart. Wer die
  // Gesellschaften nach Land gliedert, will sie morgen wieder so sehen — und
  // die Konten deshalb noch lange nicht.
  const [gruppiertNach, setGruppiertNach] = useState<string>(() =>
    gemerkteSicht(objektart, 'gruppe', GLIEDERUNG_AB_WERK[objektart]?.gruppe ?? ''),
  )
  // **Die Bezeichnung ist die Vorgabe** (R678, seit 2026-09-21): Bis dahin
  // war es der Code, und der war das, was der Anwender las. Seit die Nummer in
  // der Bezeichnung steht, ordnet sie die Liste -- und der Code ist der
  // Schluessel geworden, nach dem niemand sortieren will.
  const [sortiertNach, setSortiertNach] = useState<string>(() =>
    gemerkteSicht(objektart, 'sortierung', GLIEDERUNG_AB_WERK[objektart]?.sortierung ?? 'name'),
  )
  const [laeuft, setLaeuft] = useState(false)
  const [anlegen, setAnlegen] = useState(false)
  /**
   * Was der Weg ins Anlageblatt schon festgelegt hat (R713).
   *
   * Leer bei jeder Objektart, die mit einem einzigen „Neu anlegen" beginnt;
   * gefüllt, wo je Art eine eigene Schaltfläche steht — dann trägt sie die
   * Art hinein.
   */
  const [vorbelegung, setVorbelegung] = useState<Record<string, string>>({})
  const [lage, setLage] = useState<Loeschlage | null>(null)
  /**
   * Ob das Löschfenster offen steht (R710).
   *
   * **Getrennt von der Lage**, obwohl beides zum selben Vorgang gehört: Das
   * Fenster geht mit dem Klick auf, die Lage kommt erst danach vom Dienst.
   * Hinge das Fenster an der Lage, bliebe der Bildschirm nach dem Klick
   * einen Moment lang unverändert — und das liest sich wie ein Knopf, der
   * nichts tut.
   */
  const [loeschfenster, setLoeschfenster] = useState(false)
  // Rechtsklick → Reihenfolge ändern (7.4 Abschnitt 32, R1341).
  const [reihenfolgemenue, setReihenfolgemenue] = useState<{ x: number; y: number; ziel: Reihenfolgeziel } | null>(null)
  const [reihenfolge, setReihenfolge] = useState<Reihenfolgeziel | null>(null)
  const [meldung, setMeldung] = useState<string | null>(null)

  /**
   * Der betrachtete Stand — `null` heißt heute (R517).
   *
   * **Es ist eine Erfassungsnummer und kein Datum.** Die Nummer ist die
   * Ordnung, in der CoKo seine Schreibvorgänge zählt; zwei Änderungen
   * derselben Sekunde trennt nur sie. Der Stand steht hier und nicht im
   * Blatt, weil er das Lesen steuert und nicht das Gelesene: Wer ihn ins
   * Blatt legte, verlöre ihn bei jedem Neuladen.
   *
   * **Er gehört zur Objektart wie die Auswahl** (R1504): Wechselt die Art,
   * ist er im selben Augenblick fort.
   */
  const [blick, setBlick] = useListenwahl<number | null>(objektart, null)
  // Was der Anwender nicht darf, ist nicht da (8.1 Abschnitt 7, R1180).
  const darf = useDarf()

  /**
   * **Die Fassungsspalte ist eingeklappt, bis jemand sie aufklappt** (Auftrag
   * vom 2026-09-24).
   *
   * Sie beantwortet eine Frage, die selten gestellt wird — *wer hat wann was
   * geändert* — und nahm dafür auf jedem Blatt ein Viertel der Breite. Wer ein
   * Stammdatum pflegt, sieht zuerst die Bezeichnungen und die Eigenschaften;
   * die Geschichte dahinter sucht er, wenn etwas nicht stimmt.
   *
   * **Sie klappt wie die Bereichsleiste** (R150): zur Seite auf Symbolbreite,
   * mit einem Knopf im Kopf, und der Zustand wird je Anwender gemerkt. Wer sie
   * dauernd braucht, klappt sie einmal auf und findet sie beim nächsten Blatt
   * wieder offen — dieselbe Zusage wie an der Leiste.
   *
   * Sie liegt hier und nicht in der Spalte selbst: Eingeklappt ändert sich die
   * Breite des **Rasters**, und das steht eine Ebene höher.
   */
  const [fassungenZu, setFassungenZu] = useState(() => fassungsspalteGemerkt())

  /**
   * Die **vollständige** Fassungsreihe des gewählten Elements (R519).
   *
   * **Sie ist Navigation und keine Auskunft am Stand.** Das Blatt zeigt einen
   * Stand, die Reihe zeigt die Wege dorthin — und eine Leiste, die beim Gehen
   * hinter einem verschwindet, ist keine. Sie wird deshalb am heutigen Blick
   * gefüllt und bleibt beim Springen stehen; was **nach** dem betrachteten
   * Stand liegt, wird gekennzeichnet und nicht entfernt.
   */
  const [reihe, setReihe] = useState<Fassungsangabe[]>([])

  // Der Unterpunkt bestimmt, welche Objektarten hier hingehören — der
  // Kontenrahmen zeigt Konten *und* Kontengruppen, denn die Gruppe ist der
  // Träger der Kontoeigenschaften (0019) und ohne sie nicht zu verstehen.
  useEffect(() => setObjektart(zulaessig[0] ?? 'einheit'), [zulaessig])

  useEffect(() => {
    setBlatt(null)
    setReihe([])
    setSuche('')
    // Wonach man Konten gruppiert, sagt nichts darueber, wonach man
    // Gesellschaften gruppiert — und das Feld gibt es dort womoeglich nicht.
    setGruppiertNach(gemerkteSicht(objektart, 'gruppe', GLIEDERUNG_AB_WERK[objektart]?.gruppe ?? ''))
    setSortiertNach(
      gemerkteSicht(objektart, 'sortierung', GLIEDERUNG_AB_WERK[objektart]?.sortierung ?? 'code'),
    )
  }, [objektart])

  useEffect(() => {
    if (!vorauswahl) {
      return
    }

    setAnlegen(false)

    // Erst die Art, dann der Eintrag: Der Artwechsel leert die Auswahl (sonst
    // stünde ein Kontoschlüssel in der Positionsliste), deshalb kommt der
    // Eintrag im nächsten Durchlauf — dieser Haken hängt an beidem.
    if (vorauswahl.objektart !== objektart) {
      if (zulaessig.includes(vorauswahl.objektart)) {
        setObjektart(vorauswahl.objektart)
      }

      return
    }

    setGewaehlt(vorauswahl.schluessel)
    setBlick(null)
  }, [vorauswahl, objektart])

  // **Eine späte Antwort darf keine frische überschreiben** (R517): Wer
  // zweimal schnell in der Fassungsreihe springt, bekäme sonst den Stand,
  // dessen Antwort zufällig zuletzt eintraf — und der Kasten daneben
  // markierte einen anderen. Für das Blatt sorgt der Baustein dafür; für die
  // Reihe diese Nummer: Jeder Wechsel von Art, Element oder Stand macht die
  // laufende Frage ungültig.
  const blattfrage = useRef(0)
  useEffect(
    () => () => {
      blattfrage.current += 1
    },
    [objektart, gewaehlt, blick],
  )

  // **Das Blatt ist ein Hauptabruf** (7.2 Abschnitt 39, R1211): Kam es
  // nicht, steht in der Mitte „Ließ sich nicht laden" und nicht „Nichts
  // ausgewählt" (R1210).
  const blattabruf = useAbruf<Stammdatenblatt>(
    gewaehlt
      ? () => {
          const frage = blattfrage.current
          return api.stammdatenblatt(objektart, gewaehlt, blick).then((b) => {
            // **Die Reihe wird nur am heutigen Blick aufgefrischt** (R519). Am
            // Alt-Stand kommt sie abgeschnitten zurück — das ist für einen
            // Nachweis AN diesem Stand richtig (R517) und für eine Leiste, an der
            // man sich entlanghangelt, falsch. Ein Elementwechsel setzt sie auf
            // leer zurück, und der beginnt immer im heutigen Blick; die Reihe
            // gehört deshalb nie zu einem anderen Element.
            if (frage === blattfrage.current && blick === null) {
              setReihe(b.fassungen)
            }
            return b
          })
        }
      : null,
    [objektart, gewaehlt, blick],
  )
  const blatt = blattabruf.wert
  const setBlatt = blattabruf.setze

  /*
    **Das heutige Blatt wird einmal geholt und nicht bei jedem Sprung** (R518):
    Es hängt am Element, nicht am betrachteten Stand. Der Haken hört deshalb auf
    „sind wir im Alt-Stand" und nicht auf die Standnummer selbst — sonst läge
    bei jedem Sprung in der Reihe ein zweiter Ruf auf der Leitung, der immer
    dieselbe Antwort brächte.
  */
  const imAltstand = blick !== null

  /**
   * Das heutige Blatt daneben — nur im Alt-Stand geladen (R518).
   *
   * **Ein Unterschied braucht zwei Seiten.** Das betrachtete Blatt allein weiß
   * nicht, was sich seither geändert hat; die Fassungsreihe weiß nur, WELCHE
   * Felder sich irgendwann geändert haben, und nicht, worauf sie heute stehen.
   * Deshalb steht im Alt-Stand das heutige Blatt daneben — ungezeigt, nur zum
   * Vergleichen.
   *
   * **Ein Hauptabruf, obwohl ungezeigt** (7.2 Abschnitt 39, R1211): An ihm
   * hängt der Satz „Keine Abweichung zu heute". Bis zum 2026-10-06 lief er
   * still im Hintergrund, und ein Ausfall ergab genau diesen Satz. Je Element
   * frisch: Das heutige Blatt eines anderen ist kein Vergleich.
   */
  const heutigabruf = useAbruf<Stammdatenblatt>(
    gewaehlt && imAltstand ? () => api.stammdatenblatt(objektart, gewaehlt) : null,
    [objektart, gewaehlt, imAltstand],
    true,
  )
  const heutig = heutigabruf.wert

  /**
   * Die Auswahllisten der Verweisfelder (R327).
   *
   * **Ein Verweis wird ausgesucht, nicht getippt.** Bis zum 2026-09-11 stand
   * im Feld *Kontengruppe* der Schlüssel `KG-AV-SACH` als freier Text: Wer ihn
   * nicht auswendig wusste, konnte das Feld nicht pflegen — und wer sich
   * vertippte, bekam eine Gruppe, die es nicht gibt. Die Liste ist dieselbe,
   * die das Anlageblatt schon benutzt; sie kommt aus dem Bestand und nicht aus
   * einer zweiten Aufzählung.
   */
  const verweisarten = [
    ...new Set(
      [
        // **Der Vorrat hängt an der Objektart, nicht am offenen Blatt**
        // (Befund vom 2026-09-26). Bis dahin wurde er erst geladen, wenn
        // jemand eine Zeile angeklickt hatte -- die Übersichtstafel
        // darüber zeigte deshalb rohe Schlüssel wie `EH-000005-57`, und
        // zwar genau so lange, wie noch nichts ausgewählt war. Der Anwender
        // sah also beim ERSTEN Blick das Unlesbarste.
        ...(arten.find((a) => a.objektart === objektart)?.felder ?? []).map((f) => f.verweisart),
        ...(blatt?.felder ?? []).map((f) => f.verweisart),
        // **Woher geerbt wurde, steht mit Namen da** (R1433): Die Herkunft
        // nennt ein Objekt der Erbart, und dessen Liste braucht es dafür.
        arten.find((a) => a.objektart === objektart)?.erbart ?? null,
        // **Das Ziel der Kinder ist auch ein Verweis** (R327, R510): Beim
        // Löschen wird eine Position ausgesucht, nicht getippt — und die
        // Liste dafür steht in keinem Feld des Blattes.
        lage?.zielart ?? null,
      ].filter((a): a is string => a !== null),
    ),
  ]

  // Auswahllisten — im Hintergrund, Ersatz: keine Wahl (R1211, R1212).
  const verweisvorrat = useHintergrundabruf(
    verweisarten.length === 0
      ? null
      : () =>
          Promise.all(
            verweisarten.map(async (art) => [art, (await api.stammdatenliste(art)).eintraege] as const),
          ).then((paare) => Object.fromEntries(paare)),
    [arten, objektart, blatt?.schluessel, lage?.zielart],
    KEIN_VORRAT,
  )

  /**
   * Der Vergleich — **einmal gerechnet und an drei Stellen gelesen** (R518).
   *
   * Das Band nennt die Namen, das Blatt färbt die Zellen, der Tooltip trägt den
   * heutigen Wert. Drei Stellen, die denselben Vergleich einzeln anstellen,
   * sind drei Stellen, die irgendwann verschieden antworten.
   *
   * **Ohne heutiges Blatt kein Vergleich** (R1210): Solange es unterwegs ist
   * oder nicht kam, steht kein „Keine Abweichung" da.
   */
  const abweichungen = useMemo(
    () => (blatt && imAltstand && heutig ? unterschiede(blatt, heutig, verweisvorrat) : null),
    [blatt, imAltstand, heutig, verweisvorrat],
  )

  /**
   * Nach jedem Schreiben neu lesen: Die Antwort ist der Bestand, nicht mein
   * Entwurf.
   *
   * **Und wenn der Bestand ihn ablehnt, steht es da** (R145). Bis zum
   * 2026-09-19 hatte diese Stelle kein `catch`: Ein abgewiesener Auftrag lief
   * ins Leere, das Blatt wurde neu geladen, und das Feld sprang auf seinen
   * alten Wert zurück — **ohne ein Wort**. Gefunden an der Beteiligungsart,
   * aber älter als sie: Auch die Zwillingsabsage beim Umbenennen (R307) und
   * die Regeln des Ordnerbaums (R558) verschwanden hier seit je lautlos.
   */
  async function pflegen(auftrag: Pflegeauftrag) {
    if (gewaehlt) {
      await pflegeZeile(gewaehlt, auftrag)
    }
  }

  /**
   * Dasselbe für **irgendeine** Zeile, nicht nur für die aufgeschlagene.
   *
   * Die Tafel schreibt in Zeilen, die nicht gewählt sind — das ist ihr ganzer
   * Zweck (7.4 Abschnitt 20). Der Weg ist derselbe: schreiben, neu lesen, und
   * eine Absage steht da.
   */
  /**
   * Das Blatt noch einmal lesen, **ohne etwas zu schreiben** (R697).
   *
   * Der Zwecksatz haengt an der Objektart und nicht an der Zeile; nach einer
   * Aenderung daran muss das Blatt ihn neu holen. Ein leerer Pflegeauftrag
   * taete das auch -- und schriebe dabei eine Fassung, in der Werte
   * zurueckfallen (am 2026-09-22 an der Dev-Anlage gesehen).
   */
  async function blattNeuLesen() {
    if (gewaehlt === null) {
      return
    }

    setBlatt(await api.stammdatenblatt(objektart, gewaehlt))
  }

  async function pflegeZeile(schluessel: string, auftrag: Pflegeauftrag) {
    setLaeuft(true)
    setMeldung(null)
    try {
      await api.stammdatumPflegen(objektart, schluessel, auftrag)
    } catch (fehler) {
      // Am Ort steht nur die fachliche Absage; eine Störung meldet das
      // Störungsfenster (7.2 Abschnitt 30, R1003/R1004).
      setMeldung(fachlicherSatz(fehler))
    }

    // **Neu gelesen wird in jedem Fall**, auch nach der Absage: Das Feld soll
    // den Bestand zeigen und nicht den abgelehnten Entwurf. Das Blatt nur
    // dann, wenn es die geschriebene Zeile ist — die Tafel schreibt auch in
    // andere.
    try {
      if (gewaehlt === schluessel) {
        setBlatt(await api.stammdatenblatt(objektart, schluessel))
      }

      setListe(await api.stammdatenliste(objektart))
      aufAenderung?.()
    } finally {
      setLaeuft(false)
    }
  }

  /**
   * Ein Stammdatum anlegen (R315).
   *
   * **Der Schlüssel entsteht aus Vorsilbe und Code** und wird nicht eingegeben:
   * Ein frei gesetzter Schlüssel wird irgendwann zweimal gesetzt, und
   * korrigieren lässt er sich in einem append-only-Modell nicht.
   */
  async function lege(code: string, name: string, felder: Record<string, string>) {
    setLaeuft(true)
    setMeldung(null)

    try {
      const ergebnis = await api.stammdatumAnlegen(objektart, code, {
        felder,
        texte: name
          ? [{ sprache: aktuelleSprache(), textart: 'name' as const, text: name }]
          : [],
      })

      setListe(await api.stammdatenliste(objektart))
      setGewaehlt(ergebnis.schluessel)
      setAnlegen(false)
      aufAenderung?.()
      // Ohne die Kennung „EH-000017-34" (7.2 Abschnitt 34, R1131) — der neue
      // Eintrag steht gewählt in der Liste (R1136).
      meldeErfolg(t('erfolg.stammdaten.angelegt'))
    } catch (f) {
      setMeldung(fachlicherSatz(f))
    } finally {
      setLaeuft(false)
    }
  }

  /**
   * Aussondern — stilllegen oder entfernen, je nach Verwendung (R316).
   *
   * **Der Anwender entscheidet nicht, welcher Fall gilt, er sieht ihn.** Erst
   * wird gefragt, wo das Element vorkommt; die Antwort steht im Knopf, bevor
   * er gedrückt wird.
   */
  async function frageVerwendung() {
    if (!gewaehlt) {
      return
    }

    // **Das Fenster geht sofort auf, die Antwort kommt hinein** (R710):
    // Ein Knopf, der erst nach dem Dienstaufruf etwas zeigt, sieht aus wie
    // einer, der beim ersten Druck nicht ansprach.
    setLoeschfenster(true)
    setLage(null)
    setLaeuft(true)
    try {
      setLage(await api.loeschlage(objektart, gewaehlt))
    } finally {
      setLaeuft(false)
    }
  }

  async function sondereAus(auftrag: Loeschauftrag) {
    if (!gewaehlt) {
      return
    }

    setLaeuft(true)
    try {
      const ergebnis = await api.aussondern(objektart, gewaehlt, auftrag)
      setListe(await api.stammdatenliste(objektart))
      setLage(null)
      setLoeschfenster(false)
      aufAenderung?.()

      /*
        **Die Tat und ihr Gegenstand, sonst nichts** (7.2 Abschnitt 34, R1131 —
        Entscheidung vom 2026-10-06). Bis dahin stand hier die Kennung, die
        Fassungsreihe und wie viele Elemente auf welchen Schlüssel umgehängt
        wurden; wohin, zeigt der Baum.
      */
      meldeErfolg(
        t(ergebnis.art === 'entfernt' ? 'erfolg.stammdaten.entfernt' : 'erfolg.stammdaten.stillgelegt'),
      )
      setMeldung(null)

      if (ergebnis.art === 'entfernt') {
        setGewaehlt(null)
        setBlatt(null)
      } else {
        setBlatt(await api.stammdatenblatt(objektart, gewaehlt))
      }
    } catch (f) {
      setMeldung(fachlicherSatz(f))
    } finally {
      setLaeuft(false)
    }
  }

  // **Ein Vortrag ist kein Stammdatum des Anwenders** (R1060, R1064):
  // Startwert, Abschreibung Anfang und Übertrag der Vormonate gehören dem Werk
  // und stehen nicht in der Liste der Bewegungsarten.
  const eintraege = useMemo(
    () => (objektart === 'bewegungsart' ? ohneVortrag(liste?.eintraege ?? []) : (liste?.eintraege ?? [])),
    [liste, objektart],
  )
  const gefiltert = useMemo(() => passend(eintraege, suche), [eintraege, suche])

  /**
   * **Sortiert wird immer, gruppiert nur auf Wunsch** (R557).
   *
   * Die Sortierung wirkt **innerhalb** der Gruppe und ist von ihr unabhängig —
   * genau die Zusage aus dem Auftrag. Vorgabe ist der Code.
   */
  /**
   * Wie ein Feld im Gruppierungsmenü heißt.
   *
   * **Zwei Herkünfte, eine Zeile** (R554): Ein Werksfeld holt sein Etikett aus
   * der Sprachdatei, ein Hausfeld aus seinen eigenen Texten — es entsteht beim
   * Kunden und steht in keiner Auslieferung. Am Ende der Rückfallkette (R238)
   * steht sein Bezeichner, damit dort nie eine leere Zeile im Menü liegt.
   */
  const feldwort = (feld: string): string =>
    liste?.hausbeschriftungen?.[feld]
      ? beschriftung(liste.hausbeschriftungen[feld]!) || feld
      : t(`stammdaten.feld.${feld}` as Sprachschluessel)

  const gebuendelt = useMemo(() => {
    const sortiert = [...gefiltert].sort((x, y) =>
      wertVon(x, sortiertNach).localeCompare(wertVon(y, sortiertNach), undefined, { numeric: true }),
    )

    if (!gruppiertNach) {
      return [{ wert: null, zeilen: sortiert }]
    }

    const nach = new Map<string, Listeneintrag[]>()
    for (const e of sortiert) {
      const wert = e.werte[gruppiertNach] ?? ''
      if (!nach.has(wert)) nach.set(wert, [])
      nach.get(wert)!.push(e)
    }

    // **„Ohne Angabe" steht zuletzt und wird nie versteckt** — damit ist jede
    // Gruppierung zugleich eine Arbeitsliste: Wer nach der USt-IdNr. gliedert,
    // sieht am Ende, wer keine hat.
    //
    // **Und eine Gruppe, die für alle anderen gilt, steht zuoberst** (R708):
    // *Startwert* und *Buchwert der Periode* rahmen jeden Spiegel und gehören
    // keiner Kategorie. Alphabetisch stünden sie zwischen zwei Kategorien, als
    // wären sie eine dritte.
    const zuoberst = GRUPPE_ZUOBERST[gruppiertNach]
    const zuletzt = GRUPPE_ZULETZT[gruppiertNach]

    // Der Rang einer Gruppe: vorweg, was für alle gilt; zuletzt, was keine
    // Gruppe ist — „kein Spiegel" und „ohne Angabe" sagen dasselbe, einmal
    // gepflegt und einmal offen.
    const rang = (wert: string) => (wert === zuoberst ? 0 : wert === '' ? 3 : wert === zuletzt ? 2 : 1)

    return [...nach.entries()]
      .sort(([x], [y]) => rang(x) - rang(y) || x.localeCompare(y))
      .map(([wert, zeilen]) => ({ wert, zeilen }))
  }, [gefiltert, gruppiertNach, sortiertNach])

  // Hier stand bis zum 2026-10-06 das Ziehen in der Liste (R709) — für die
  // Bewegungsarten. Die Reihenfolge ändert seither das Fenster per Rechtsklick
  // (7.4 Abschnitt 32, R1341).
  // Die Objektarten, deren Reihenfolge das Fenster ändert (R1341).
  const reihenfolgeart = REIHENFOLGEARTEN.has(objektart) && darf('reihenfolgeSetzen')


  const objektartname = t(`stammdaten.art.${objektart}` as Sprachschluessel)

  const artbeschreibung = arten.find((a) => a.objektart === objektart) ?? null

  /**
   * Das Feld, das die **Zielart eines Verweises** trägt — und die Werte, die
   * es annehmen kann (R713).
   *
   * **Nur das Organisationselement hat eines**, und daraus folgt die
   * Bedienung: je Wert eine Schaltfläche über der Liste. Die Regel ist
   * allgemein formuliert und nicht an den Namen gebunden — wer je ein zweites
   * solches Objekt baut, bekommt dieselbe Bedienung, ohne sie zu bestellen.
   */
  const artfeld =
    artbeschreibung?.felder.find((f) => f.verweisartfeld)?.verweisartfeld ?? null

  const artwahl =
    (artfeld ? artbeschreibung?.felder.find((f) => f.feld === artfeld)?.vorrat : null) ?? []

  /**
   * **Wo das Codefeld auf ein anderes Objekt zeigt, steht dessen Name**
   * (R705, Entscheidung vom 2026-09-22).
   *
   * Zwei Objektarten führen ein Codefeld, das keinen Code enthält, sondern
   * einen **Schlüssel**: Eine Kontozuordnung heißt nach ihrem Konto
   * (`KTO-0100`), eine Gliederungszeile nach ihrer Position (`POS-A-I-3`).
   * Beide tragen selbst keine Bezeichnung — in der Liste stand deshalb der
   * Schlüssel des verwiesenen Objekts, und sonst nichts.
   *
   * **Die Auflage dazu steht in der Spalte daneben:** Sobald zwei Zuordnungen
   * desselben Kontos denselben Namen zeigen, unterscheidet sie nur noch ihre
   * Gültigkeit. Sie steht als Merkmal in derselben Zeile und darf nicht
   * wegfallen.
   */
  const codeverweisart =
    artbeschreibung?.codefeld
      ? (artbeschreibung.felder.find((f) => f.feld === artbeschreibung.codefeld)?.verweisart ?? null)
      : null

  // Namen nachschlagen, im Hintergrund (R1007, R1211) — Ersatz: keine Namen,
  // dann steht der Schlüssel da (R1212).
  const codevorrat = useHintergrundabruf<Record<string, Beschriftung>>(
    codeverweisart
      ? () =>
          api
            .stammdatenliste(codeverweisart)
            .then((l) => Object.fromEntries(l.eintraege.map((e) => [e.schluessel, e.namen])))
      : null,
    [codeverweisart],
    KEINE_NAMEN,
  )

  /** Wie der Code einer Zeile heißt — beim Verweis der Name des Ziels. */
  const codewort = (code: string): string =>
    codeverweisart ? beschriftung(codevorrat[code] ?? {}) || code : code

  /**
   * **Wie das Blatt heißt — in der Überschrift und im Titel des
   * Löschfensters** (7.4 Abschnitt 34.7, R1450).
   *
   * Bis zum 2026-10-09 stand über der Beteiligung an der Tochter 1
   * „EH-000008-48", und das Löschfenster hieß „EH-000008-48 löschen". Eine
   * Beziehung heißt jetzt nach ihrem Hauptende, wie in der Liste darüber;
   * alles andere nach Nummer und Name.
   */
  const blattname = (b: Stammdatenblatt): string =>
    codeverweisart
      ? codewort(b.code)
      : [b.codefeld !== null ? b.code : '', beschriftung(namenAus(b.sprachen))].filter(Boolean).join(' ') ||
        b.code

  /**
   * **Ein Element ohne eigenen Namen heißt wie das, worauf es zeigt** (R713).
   *
   * Dasselbe wie bei der Kontozuordnung eine Ebene höher (R705) — nur dass
   * die Zielart hier je Zeile wechselt: Ein Organisationselement zeigt auf
   * einen Kreis, eine Gesellschaft, einen Ordner oder einen Dimensionswert.
   * Deshalb werden alle vier Vorräte gelesen und zu einem Verzeichnis
   * zusammengelegt; die Schlüssel tragen ihre Art ohnehin in sich und können
   * sich nicht in die Quere kommen.
   *
   * **Ohne das stünde in der Liste `ORG-EH-000005-57`** — der Schlüssel, und
   * den soll niemand lesen müssen (R678, R703).
   */
  const verweisfeld = artbeschreibung?.felder.find((f) => f.verweisartfeld)?.feld ?? null

  // Namen nachschlagen, im Hintergrund (R1007, R1211) — Ersatz: keine Namen,
  // dann steht der Schlüssel da (R1212).
  const verweisnamen = useHintergrundabruf<Record<string, Beschriftung>>(
    verweisfeld
      ? () =>
          api.organisationsarten().then(async (arten) => {
            const listen = await Promise.all(
              Object.values(arten).map((a) => api.stammdatenliste(a)),
            )
            return Object.fromEntries(
              listen.flatMap((l) => l.eintraege.map((e) => [e.schluessel, e.namen] as const)),
            )
          })
      : null,
    [verweisfeld],
    KEINE_NAMEN,
  )

  /** Der Name des verwiesenen Objekts — `null`, wo es keinen Verweis gibt. */
  const verweisname = (e: Listeneintrag): string | null => {
    if (!verweisfeld) {
      return null
    }

    const ziel = e.werte[verweisfeld]
    return ziel ? beschriftung(verweisnamen[ziel] ?? {}) || ziel : null
  }

  /**
   * **Was zwei gleich aussehende Zeilen noch unterscheidet** — die Auflage zu
   * R705.
   *
   * Sobald eine Kontozuordnung den Namen ihres Kontos zeigt, sehen zwei
   * Zuordnungen desselben Kontos aus verschiedenen Perioden gleich aus; ihr
   * Schlüssel steht nicht mehr da, und eine eigene Bezeichnung haben sie nie
   * gehabt. Die **Gültigkeit** ist dann das Einzige, was bleibt — sie tritt an
   * die Stelle, an der sonst das Kürzel steht.
   */
  const gueltigkeit = (e: Listeneintrag): string | null => {
    const von = codeverweisart ? e.werte['gueltig_von'] : ''
    // **Gelesen wird „gültig ab Januar 2026"** (R839 — Eine Periode liest sich
    // als Monat und Jahr): Bis zum 2026-09-30 stand hier die Feldform
    // „01.2026" — die gehört ins Eingabefeld und in keinen Satz.
    return von ? t('stammdaten.zeile.gueltigab', { periode: periodenwort(von) }) : null
  }

  return (
    <>
      {/*
        **Der ganze Bereich als eine Tabelle** (R615ff., Auftrag vom
        2026-09-19). Sie steht über den drei Spalten und nicht in einer davon:
        Dreizehn Spalten in 300 px wären keine Tabelle, sondern eine Liste mit
        Strichen.

        **Sie ersetzt das Blatt nicht, sie steht davor.** Ein Klick auf die
        Kennung links schlägt dasselbe Blatt auf wie bisher — mit Herkunft,
        Vorschlag und Fassungsreihe. Die Tafel beantwortet die andere Frage:
        ob es überall gleich aussieht.
      */}
      {/* Ohne die Beschreibung der Objektarten fehlen Tafel und Artenwahl — und das steht da (R1210). */}
      {artenabruf.ausgefallen && <Abrufausfall abruf={artenabruf} />}

      {tafel && !nurBlatt && artbeschreibung && liste && (
        <Stammdatentafel
          art={artbeschreibung}
          eintraege={gefiltert}
          hausbeschriftungen={liste.hausbeschriftungen}
          hausfeldbeschreibungen={liste.hausfeldbeschreibungen ?? {}}
          vorrat={verweisvorrat}
          codewort={codewort}
          laeuft={laeuft}
          gewaehlt={gewaehlt}
          aufWahl={(schluessel) => {
            setAnlegen(false)
            setGewaehlt(schluessel)
          }}
          aufSchreiben={pflegeZeile}
          nurLesen={!darf('stammdatumPflegen')}
        />
      )}

    <section
      className={['stammdaten', fassungenZu ? 'fassungenzu' : '', nurBlatt ? 'nurblatt' : ''].filter(Boolean).join(' ')}
      style={{ '--stammdatenbreite': `${spaltenbreite}px` } as React.CSSProperties}
    >
      {!nurBlatt && (
      <nav className="stammdatenarten" aria-label={t('stammdaten.arten')}>
        {/*
          **Die Spalte läßt sich ziehen** (Auftrag vom 2026-09-27: „Mach die
          Breite des mittleren Fensters bitte variabel. Die meisten
          Bewegungsarten lassen sich nicht lesen.").

          **Und sie läßt sich auch ohne Maus verstellen** (R143): Der Griff
          nimmt den Fokus und hört auf die Pfeiltasten — keine Funktion kennt
          nur die Maus. Was er einstellt, bleibt gemerkt, je Anwender im
          Browser, wie jede andere Sicht dieses Bildschirms.
        */}
        {/*
          **Kein Knopf, sondern eine Trennlinie.** Ein Knopf verspricht, daß
          ein Klick etwas tut (R451) — dieser Griff tut auf einen Klick
          nichts und soll es auch nicht: Er wird gezogen. Die Funktionsprüfung
          hat genau das gemeldet, als er noch ein `button` war.
        */}
        <div
          className="spaltenzug"
          role="separator"
          tabIndex={0}
          aria-orientation="vertical"
          aria-label={t('stammdaten.breite')}
          aria-valuenow={spaltenbreite}
          aria-valuemin={220}
          aria-valuemax={640}
          onPointerDown={(e: React.PointerEvent<HTMLDivElement>) => {
            const griff = e.currentTarget
            const start = e.clientX
            const anfang = spaltenbreite
            griff.setPointerCapture(e.pointerId)
            griff.classList.add('zieht')

            const zieh = (x: PointerEvent) => setzeBreite(anfang + (x.clientX - start))
            const ende = () => {
              griff.classList.remove('zieht')
              griff.removeEventListener('pointermove', zieh)
              griff.removeEventListener('pointerup', ende)
            }

            griff.addEventListener('pointermove', zieh)
            griff.addEventListener('pointerup', ende)
          }}
          onKeyDown={(e) => {
            const schritt = e.shiftKey ? 40 : 10
            if (e.key === 'ArrowLeft') {
              e.preventDefault()
              setzeBreite(spaltenbreite - schritt)
            } else if (e.key === 'ArrowRight') {
              e.preventDefault()
              setzeBreite(spaltenbreite + schritt)
            }
          }}
        />
        {/* Bei nur einer Objektart wäre die Liste eine Zeile, die nichts
            entscheidet — dann steht sie nicht da. */}
        {arten.length > 1 && (
          <>
            <h3>{t('stammdaten.arten')}</h3>
            <ul>
              {arten.map((a) => (
                <li key={a.objektart}>
                  {/*
                    **Die Auswahl steht in `aria-current`, nicht nur in einer
                    Klasse.** Eine Klasse färbt; sie sagt einem Screenreader
                    nichts, und für einen Anwender, der die aktuelle Objektart
                    noch einmal anklickt, geschieht sichtbar nichts — ohne dass
                    erkennbar wäre, warum.
                  */}
                  <button
                    type="button"
                    aria-current={a.objektart === objektart ? 'true' : undefined}
                    className={`objektartknopf ${a.objektart === objektart ? 'aktiv' : ''}`.trim()}
                    onClick={() => {
                      setObjektart(a.objektart)
                      setGewaehlt(null)
                    }}
                  >
                    {t(`stammdaten.art.${a.objektart}` as Sprachschluessel)}
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}

        {/* „Keine Einträge" nur, wenn die Liste kam (R1210). */}
        {listeabruf.ausgefallen && <Abrufausfall abruf={listeabruf} />}

        {liste && (
          <>
            <h3>{t('stammdaten.eintraege', { anzahl: anzahlwort(eintraege.length) })}</h3>
            {!ohneAnlegen?.includes(objektart) && darf('stammdatumAnlegen') && (
            <p className={artwahl.length > 0 ? 'handreichung aufnahmewege' : 'handreichung'}>
              {/*
                **Je Art eine Schaltfläche, wo die Art den Vorrat entscheidet**
                (R713, 2026-09-24). Ein einziges „Neu anlegen" führte hier in
                ein Blatt, das erst nach der Artwahl weiß, was es anbieten
                soll; die Schaltfläche beantwortet die Frage, bevor sie
                entsteht — und sagt zugleich, dass hier **aufgenommen** und
                nicht angelegt wird.
              */}
              {/*
                **R727-Ausnahme**: Die beiden Schaltflaechen stehen in den zwei
                Zweigen derselben Frage und nie zusammen auf dem Bildschirm --
                entweder je Art eine (R713) oder die eine ohne Artwahl. Mehrere
                Aufnehmen-Knoepfe nebeneinander sind gleichrangige Wege in
                dieselbe Handlung und damit die begruendete Ausnahme der Regel.
              */}
              {/*
                **UND GENAU DESHALB TRAGEN SIE NICHT DIE MARKE** (R803,
                Befund des Auftraggebers vom 2026-09-26: *„Das ist farblich
                etwas uebers Ziel hinausgeschossen"*). Die Markenflaeche hebt
                EINE erwuenschte Handlung hervor; vier davon nebeneinander
                heben nichts mehr hervor und ergeben eine rote Wand ueber der
                Liste. Wo vier Wege gleichrangig sind, gibt es keinen
                Hauptweg -- der Zweig ohne Artwahl hat einen und behaelt sie.
              */}
              {artwahl.length > 0 ? (
                artwahl.map((wert) => (
                  <button
                    key={wert}
                    type="button"
                    onClick={() => {
                      setVorbelegung({ [artfeld!]: wert })
                      setAnlegen(true)
                      setGewaehlt(null)
                      setBlick(null)
                      setMeldung(null)
                    }}
                  >
                    {t('stammdaten.aufnehmen', { art: wertwort(artfeld!, wert) })}
                  </button>
                ))
              ) : (
              <button
                type="button"
                className="haupt"
                onClick={() => {
                  setVorbelegung({})
                  setAnlegen(true)
                  setGewaehlt(null)
                  // Angelegt wird heute. Bliebe der Alt-Stand stehen, führte
                  // der Weg zurück aus dem Anlageblatt in eine Vergangenheit.
                  setBlick(null)
                  setMeldung(null)
                }}
              >
                {t('stammdaten.neu')}
              </button>
              )}
            </p>
            )}
            <div className="stammdatensuche">
              <input
                type="search"
                value={suche}
                placeholder={t('stammdaten.suche')}
                aria-label={t('stammdaten.suche')}
                onChange={(e) => setSuche(e.target.value)}
              />
            </div>
            {/*
              **Gruppieren und Sortieren stehen über der Liste, nicht in ihr.**
              Bei 200 Gesellschaften ist die Gliederung die erste Frage, und
              eine Einstellung, die man erst aufklappen muss, wird nicht
              gefunden (R557). Wonach sich gruppieren lässt, sagt der Katalog.
            */}
            {(liste?.gruppierfelder.length ?? 0) > 0 && (
              <div className="stammdatensicht">
                <label>
                  <span>{t('stammdaten.gruppieren')}</span>
                  <select
                    value={gruppiertNach}
                    onChange={(e) => {
                      setGruppiertNach(e.target.value)
                      merkeSicht(objektart, 'gruppe', e.target.value)
                    }}
                  >
                    <option value="">{t('stammdaten.gruppieren.keine')}</option>
                    {liste!.gruppierfelder.map((f) => (
                      <option key={f} value={f}>
                        {feldwort(f)}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>{t('stammdaten.sortieren')}</span>
                  <select
                    value={sortiertNach}
                    onChange={(e) => {
                      setSortiertNach(e.target.value)
                      merkeSicht(objektart, 'sortierung', e.target.value)
                    }}
                  >
                    <option value="name">{t('stammdaten.sortieren.name')}</option>
                    {/*
                      **Nach dem Code sortiert nur, wo es einen gibt** (R676).
                      Sonst ist er der Schluessel -- eine Reihenfolge, die
                      niemand sucht und keine Frage beantwortet.
                    */}
                    {artbeschreibung?.codefeld !== null && (
                      <option value="code">{t('stammdaten.sortieren.code')}</option>
                    )}
                    {/*
                      **Die Reihenfolge ist keine Gruppierung und stand
                      deshalb nicht im Menue** (R708): Die Liste bot an, wonach
                      sich gliedern laesst, und nach einer Sortierzahl gliedert
                      niemand. Sie ist aber genau das, wonach eine
                      Bewegungsart geordnet ist -- und ohne diesen Eintrag
                      gaebe es keinen Weg zurueck, wenn der Anwender einmal
                      etwas anderes gewaehlt hat.
                    */}
                    {/*
                      **„Im Spiegel" gilt nur für die Bewegungsart** (Fund vom
                      2026-09-24): Der Eintrag steht bei jeder Objektart mit
                      einer Sortierzahl — beim Partnercode, beim Ordner, beim
                      Organisationselement — und dort ist der Spiegel eine
                      Behauptung über etwas, das der Bereich gar nicht kennt.
                    */}
                    {artbeschreibung?.felder.some((f) => f.feld === 'sortierung') && (
                      <option value="sortierung">
                        {t(
                          objektart === 'bewegungsart'
                            ? 'stammdaten.sortieren.reihenfolge'
                            : 'stammdaten.sortieren.reihenfolge.allgemein',
                        )}
                      </option>
                    )}
                    {liste!.gruppierfelder.map((f) => (
                      <option key={f} value={f}>
                        {feldwort(f)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            )}
            {/*
              **Steht die Tafel darüber, steht die Liste nicht darunter.**
              Dieselben achtzehn Einträge ein zweites Mal sind keine zweite
              Auskunft, sie sind eine halbe Bildschirmhöhe (R145, R606). Was
              die Leiste behält, sind die Griffe, die die Tafel nicht hat:
              anlegen, suchen, gruppieren, sortieren — sie wirken auf die
              Tafel, weil beide auf derselben gefilterten Menge stehen.
            */}
            {!tafel && (
            <ul className="stammdatenliste">
              {gebuendelt.map((gruppe) => (
                <Fragment key={gruppe.wert ?? '*'}>
                {gruppe.wert !== null && (
                  <li className="gruppenkopf">
                    <span className="was">
                      {/*
                        **Dieselbe Übersetzung wie im Blatt** (berichtigt am
                        2026-09-21): Der Gruppenkopf suchte nur
                        `stammdaten.wert.<feld>.<wert>` und fand `Kreditspiegel`
                        nicht, weil der Schlüssel dafür ohne Feldnamen steht.
                        Die Liste zeigte `VERBINDLICHKEITEN`, das Blatt daneben
                        `Kreditspiegel` — zwei Wege für eine Aussage, und einer
                        davon war kürzer als der andere.
                      */}
                      {gruppe.wert === ''
                        ? t('stammdaten.gruppe.ohne')
                        : wertwort(gruppiertNach, gruppe.wert)}
                    </span>
                    {/* Die Zahl ist die halbe Auskunft: „wie viele davon
                        eigentlich", ohne dass jemand aufklappt. */}
                    <span className="anzahl">{anzahlwort(gruppe.zeilen.length)}</span>
                  </li>
                )}
                {gruppe.zeilen.map((e) => (
                <Fragment key={e.schluessel}>
                {/* Hier stand bis zum 2026-10-06 die Lücke zum Ablegen einer
                    gezogenen Zeile (R709). Die Reihenfolge ändert seither das
                    Fenster per Rechtsklick (7.4 Abschnitt 32, R1341). */}
                {/*
                  **Die Einrückung steht im Abstand nach links** (R319, R708)
                  und nicht in einer geschachtelten Liste: Eine Liste in der
                  Liste wäre ein Kasten im Kasten, und der Gruppenkopf ist
                  keine Zeile, unter die etwas gehängt wird — er ist eine
                  Überschrift.
                */}
                <li
                  className={gruppe.wert !== null ? 'untergruppe' : undefined}
                >
                  <button
                    type="button"
                    aria-current={e.schluessel === gewaehlt ? 'true' : undefined}
                    className={`stammdatenzeile ${e.schluessel === gewaehlt ? 'aktiv' : ''}`.trim()}
                    // **Rechtsklick → Reihenfolge ändern** (7.4 Abschnitt 32, R1341).
                    onContextMenu={(ereignis) => {
                      if (!reihenfolgeart) return
                      ereignis.preventDefault()
                      setReihenfolgemenue({
                        x: ereignis.clientX,
                        y: ereignis.clientY,
                        ziel: { art: objektart, element: e.schluessel, titel: [e.code, beschriftung(e.namen)].filter(Boolean).join(' ') },
                      })
                    }}
                    // Auch das Anlageblatt geht dabei zu: Ein Klick in die
                    // Liste ist die Ansage "das hier will ich sehen" — bliebe
                    // das Blatt stehen, sähe es aus, als täte der Klick nichts.
                    onClick={() => {
                      setGewaehlt(e.schluessel)
                      setAnlegen(false)
                      setLage(null)
                      setLoeschfenster(false)
                      // **Der Stand gehört dem Element, nicht dem Bildschirm**
                      // (R517): Ein Blick in den September 2026 ist für dieses
                      // Konto eine Aussage und für das nächste eine Zufallszahl.
                      setBlick(null)
                    }}
                  >
                    {/* **Der Code steht nur, wo es einen gibt** (R676): Sonst
                        wäre es der Schlüssel, und den soll der Anwender nicht
                        lesen müssen — die Nummer steht ihm in der Bezeichnung
                        voran (R678). */}
                    {artbeschreibung?.codefeld !== null && (
                      <span className={codeverweisart ? 'name' : 'code'}>{codewort(e.code)}</span>
                    )}
                    {/* **Die Sortierzahl steht nicht mehr in der Zeile**
                        (Auftrag vom 2026-09-27, R830). Sie stand hier, weil
                        die Reihenfolge zwei Wege hatte — ziehen und tippen —
                        und keiner von beiden stumm sein sollte (R709).

                        Seit die Bewegungsart ihre Nummer im NAMEN trägt,
                        stünde dieselbe Zahl zweimal in derselben Zeile: „10"
                        und „010 Zugang". Die eigene Spalte beantwortet damit
                        keine Frage mehr, die die Zeile nicht schon
                        beantwortet — und genau dann fällt sie weg. Gezogen
                        wird weiterhin am Griff links. */}
                    {/* **Die Kette endet beim Code, nie beim Schluessel** (R238) —
                        und der steht in dieser Zeile schon links. Ein fehlender Name
                        faellt deshalb auf nichts zurueck statt auf `ORD-000005-96`.
                        Aufgefallen am 2026-09-15 am ersten Ordner ohne Namen.
                        **Ohne Codefeld steht links nichts mehr**, und dann ist die
                        Bezeichnung das Einzige — ein Eintrag ohne sie zeigt darum
                        seinen Schlüssel, damit die Zeile nicht leer ist. */}
                    {beschriftung(e.namen) ? (
                      <span className="name">{beschriftung(e.namen)}</span>
                    ) : verweisname(e) ? (
                      <>
                        {/* **Die Art steht daneben** (R713): Ohne sie sähen
                            die Gesellschaft *Vertrieb* und der gleichnamige
                            Ordner in dieser Liste gleich aus. */}
                        <span className="name">{verweisname(e)}</span>
                        <span className="kurz">
                          {wertwort('art', e.werte['art'] ?? null)}
                        </span>
                      </>
                    ) : (
                      artbeschreibung?.codefeld === null && <span className="code">{e.schluessel}</span>
                    )}
                    {/* **Das Kürzel steht nicht mehr in der Liste** (Auftrag
                        vom 2026-09-27: „Ein Name reicht in der Anzeige
                        völlig aus."). Es stand daneben, wo es sich vom Namen
                        unterschied — bei den Bewegungsarten also fast
                        überall, und dort las man dann „100 Zugang zu
                        Anschaffungskosten · Zugang". Das Kürzel ist für die
                        Spalte eines Spiegels gemacht, wo der Platz knapp
                        ist; in einer Liste, die die ganze Breite hat, sagt
                        es nichts, was der Name nicht schon sagt.

                        Gepflegt wird es weiter — auf dem Blatt, unter den
                        Beschriftungen. */}
                    {gueltigkeit(e) && <span className="kurz">{gueltigkeit(e)}</span>}
                    {zeilenzusatz?.(e)}
                  </button>
                </li>
                </Fragment>
                ))}
                </Fragment>
              ))}
            </ul>
            )}
            {gefiltert.length === 0 && <p className="hinweis">{t('stammdaten.nichts')}</p>}
          </>
        )}
      </nav>
      )}

      {/* Rechtsklick in der Liste → Reihenfolge ändern (7.4 Abschnitt 32, R1341). */}
      {reihenfolgemenue && (
        <Zeilenmenue
          x={reihenfolgemenue.x}
          y={reihenfolgemenue.y}
          punkte={[
            {
              schluessel: 'reihenfolge.menue',
              tun: () => {
                setReihenfolge(reihenfolgemenue.ziel)
                setReihenfolgemenue(null)
              },
            },
          ]}
          aufSchliessen={() => setReihenfolgemenue(null)}
        />
      )}
      {reihenfolge && (
        <Reihenfolgefenster
          ziel={reihenfolge}
          aufSchliessen={(geaendert) => {
            setReihenfolge(null)
            if (geaendert) listeabruf.erneut()
          }}
        />
      )}

      {anlegen ? (
        <div className="stammdatenblatt">
          {meldung && <p className="hinweis">{meldung}</p>}
          <Anlageblatt
            objektartname={objektartname}
            art={arten.find((a) => a.objektart === objektart) ?? null}
            laeuft={laeuft}
            vorbelegt={vorbelegung}
            aufAnlegen={lege}
            aufAbbruch={() => setAnlegen(false)}
          />
        </div>
      ) : blatt ? (
        <div className="stammdatenblatt">
          {meldung && <p className="hinweis">{meldung}</p>}
          {/*
            **Das Merkzeichen steht über dem Blatt und nicht daneben** (R517):
            Wer in einer Vergangenheit liest, muss es sehen, bevor er das erste
            Feld liest — nicht, nachdem er sich über einen alten Wert gewundert
            hat.
          */}
          {blick !== null && (
            <Standblick
              blatt={blatt}
              abweichungen={abweichungen}
              aufHeute={() => setBlick(null)}
            />
          )}
          {/* Der Vergleich mit heute kam nicht — kein „Keine Abweichung" (R1210). */}
          {blick !== null && heutigabruf.ausgefallen && <Abrufausfall abruf={heutigabruf} />}

          <Blatt
            blatt={blatt}
            codename={codeverweisart ? codewort(blatt.code) : undefined}
            ohneEigenschaften={ohneEigenschaften}
            mitNummer={mitNummer}
            vorrat={verweisvorrat}
            beschriftbar={arten.find((a) => a.objektart === objektart)?.beschriftbar !== false}
            sprachen={sprachen}
            laeuft={laeuft}
            // Ohne Recht zum Pflegen liest man das Blatt wie einen alten Stand (R1180).
            nurLesen={blick !== null || !darf('stammdatumPflegen')}
            abweichungen={abweichungen}
            aufPflege={pflegen}
            aufNeuLaden={() => void blattNeuLesen()}
            /*
              **Der Löschknopf steht oben am Blatt, die Maske im Fenster**
              (R710). Man löscht, was man gerade ansieht — der Knopf gehört
              deshalb zu der Überschrift, die sagt, was das ist. Die Maske
              selbst gehört nicht auf dasselbe Blatt: Sie fragt nach Ziel,
              Weg und Periode, und solange sie unter den Eigenschaften hing,
              stand die Wahl neben den Feldern, mit denen sie nichts zu tun
              hat — und musste erst gesucht werden.

              **Im Alt-Stand steht der Knopf gar nicht da** (R517). Ein
              Löschweg unter einer Vergangenheit ist eine Falle: Gelöscht
              würde heute, und die Fundstellen daneben wären die von damals.
              Ein gesperrter Knopf täte es nicht — er lüde ein und
              verweigerte sich dann.
            */
            kopfaktion={
              blick === null && darf('aussondern') && !ohneAussondern ? (
                <button
                  type="button"
                  className="blattloeschen"
                  disabled={laeuft}
                  onClick={frageVerwendung}
                >
                  {t('stammdaten.aussondern')}
                </button>
              ) : null
            }
          />

          {/*
            Was ein Arbeitsbereich dem Blatt hinzufügt — nur im heutigen
            Blick (R517): Die Spaltenzuordnung einer Quelle in einer Vergangenheit
            zu pflegen, schriebe heute, was gestern galt.
          */}
          {blick === null && zusatz?.(blatt)}

          {/*
            Das Löschfenster hängt am Blatt und nicht an der Liste: Man
            sondert aus, was man gerade angesehen hat — und man sieht vorher,
            wo es vorkommt (R316).
          */}
          {loeschfenster && blick === null && (
            <Loeschfenster
              lage={lage}
              code={blattname(blatt)}
              vorrat={lage?.zielart ? verweisvorrat[lage.zielart] ?? [] : []}
              laeuft={laeuft}
              aufLoeschen={sondereAus}
              aufSchliessen={() => {
                setLoeschfenster(false)
                setLage(null)
              }}
            />
          )}
        </div>
      ) : (
        <div className="stammdatenblatt">
          {meldung && <p className="hinweis">{meldung}</p>}
          {/*
            **Der Bereich erklärt sich, bevor etwas gewählt ist** (7.1): Wo ein
            eigener Hinweis hinterlegt ist, steht er hier statt *Nichts
            ausgewählt* — er ersetzt den Satz und steht nicht daneben, weil
            zwei Blöcke zwei Fragen beantworten müssten und es nur eine gibt.
          */}
          {/*
            **„Nichts ausgewählt" nur, wenn nichts ausgewählt ist** (R1210):
            Kam das Blatt nicht, steht „Ließ sich nicht laden"; kommt es noch,
            steht nichts.
          */}
          {blattabruf.ausgefallen ? (
            <Abrufausfall abruf={blattabruf} />
          ) : gewaehlt !== null && blattabruf.laedt ? null : (
            <div className="meldung neutral">
              <h2>{t(LEERHINWEIS[objektart]?.titel ?? 'stammdaten.leer.titel')}</h2>
              <p>{t(LEERHINWEIS[objektart]?.text ?? 'stammdaten.leer.text')}</p>
            </div>
          )}
        </div>
      )}

      {blatt && !anlegen && (
        <Herkunft
          reihe={reihe.length > 0 ? reihe : blatt.fassungen}
          blick={blick}
          aufBlick={setBlick}
          eingeklappt={fassungenZu}
          aufKlappen={(zu) => {
            setFassungenZu(zu)
            merkeFassungsspalte(zu)
          }}
        />
      )}
    </section>
    </>
  )
}

/* --------------------------------------------------------------------------
   Die Mitte: das Blatt
   -------------------------------------------------------------------------- */

function Blatt({
  kopfaktion,
  blatt,
  codename,
  ohneEigenschaften,
  mitNummer,
  vorrat,
  beschriftbar,
  sprachen,
  laeuft,
  nurLesen,
  abweichungen,
  aufPflege,
  aufNeuLaden,
}: {
  blatt: Stammdatenblatt
  /** Die wählbaren Einträge je Verweisart (R327). */
  vorrat: Record<string, Listeneintrag[]>
  /** Zeigt der Code auf ein anderes Stammdatum, dessen Name — er steht statt der Kennung (R1450). */
  codename?: string
  /** Ob es zu dieser Objektart Namen und Kürzel gibt (R328). */
  beschriftbar: boolean
  /** Was das Haus an Sprachen erklärt hat (R556). */
  sprachen: Haussprache[]
  laeuft: boolean
  /** Ob das Blatt einen alten Stand zeigt (R517) oder der Anwender nicht pflegen darf (R1180) und deshalb nicht gepflegt wird. */
  nurLesen: boolean
  /** Was heute anders steht — `null` im heutigen Blick, wo es nichts zu vergleichen gibt (R518). */
  abweichungen: Map<string, Abweichung> | null
  aufPflege: (auftrag: Pflegeauftrag) => Promise<void>
  /**
   * Das Blatt noch einmal holen, **ohne etwas zu schreiben** (R697).
   *
   * **Gebraucht seit dem Zwecksatz**: Er steht nicht in der Zeile des Objekts,
   * sondern an der Objektart — nach einer Änderung muss das Blatt ihn neu
   * lesen, und die Pflege, die das sonst nebenbei tut, hat hier nichts zu tun.
   *
   * **Ein leerer Pflegeauftrag ist dafür kein Ersatz.** Am 2026-09-22 stand an
   * dieser Stelle `aufPflege({})` — und das schrieb eine zweite Fassung, in
   * der der Anfangsmonat des Wirtschaftsjahres von *April* auf *Januar*
   * zurückfiel. Gefunden an der Dev-Anlage, nicht von einem Test.
   */
  aufNeuLaden: () => void
  /** Was oben neben der Überschrift steht — heute das Löschen (R710). */
  kopfaktion?: ReactNode
  /** Ohne den Block Eigenschaften (R1328). */
  ohneEigenschaften?: boolean
  /** Die Nummer als Feld (R1336). */
  mitNummer?: boolean
}) {
  /*
    **Zwei Gründe, dieselbe geschlossene Tür** (R517): Ein laufender Auftrag
    sperrt die Felder, ein Alt-Stand ebenso. Für das Feld ist das derselbe
    Zustand — `disabled` —, und es wäre eine Erfindung, ihm zwei Wege dorthin
    zu bauen. **Warum** sie zu ist, sagt nicht das Feld, sondern das
    Merkzeichen über dem Blatt: Ein gesperrtes Feld ohne Grund daneben ist die
    Sorte Sperre, die man für einen Fehler hält (R143).
  */
  const gesperrt = laeuft || nurLesen

  return (
    <div className="stammdatenblatt">
      {/* Wo es keinen Namen gibt, steht auch keiner da (R328): Eine Zuordnung
          heißt nach dem, was sie verbindet — ihren Schlüssel hinter den Code zu
          setzen, sähe aus wie eine fehlende Bezeichnung. */}
      {/* **Der Knopf steht in der Zeile der Überschrift** (R710): Er gehört zu
          dem, was dort benannt ist, und nicht zu einem Abschnitt darunter. */}
      <div className="blattkopf">
        <h1>
          {/* Die Nummer ist die Änderung, die am häufigsten vorkommt (R503) — und
              die einzige, die nicht in einer Tabelle steht. Sie trägt die Färbung
              deshalb hier.

              **Und sie steht nur, wo es einen Code gibt** (R676): Sonst ist der
              Code der Schlüssel, und der steht eine Zeile tiefer zum Kopieren.
              Zweimal dieselbe Zeichenfolge in einer Überschrift wäre die Sorte
              Wiederholung, die den Bildschirm füllt und nichts sagt. */}
          {/* Wo die Nummer als Feld darunter steht, nicht noch hier (R1455). */}
          {blatt.codefeld !== null && !mitNummer && (
            <>
              <span
                className={`${codename !== undefined ? 'name' : 'code'} ${abweichungen?.has('code') ? 'heuteanders' : ''}`.trim()}
                title={heutesatz(abweichungen?.get('code'))}
              >
                {codename ?? blatt.code}
              </span>{' '}
            </>
          )}
          {beschriftbar ? beschriftung(namenAus(blatt.sprachen)) || blatt.schluessel : ''}
        </h1>
        {/* **Oben rechts und zurückgenommen** (7.2 Abschnitt 37, R1144 —
            Entscheidung vom 2026-10-06): Bis dahin stand die Objekt-ID als
            eigene Zeile unter der Überschrift, mit gerahmter Marke. Sie bleibt
            greifbar für den Austausch, ohne Vorrang vor dem Blatt. */}
        <Objektkennungszeile kennung={blatt.schluessel} />
        {kopfaktion}
      </div>

      {/*
        **Die Nummer, änderbar** (2.8 Abschnitt 13.41, R1336): geschrieben beim
        Verlassen des Feldes, wie die Bezeichnungen darunter. Eine doppelte
        Nummer weist der Server ab, und die Absage steht über dem Blatt.
      */}
      {mitNummer && blatt.codefeld !== null && (
        <p className="blattnummer">
          <label className="feld einzeln">
            <span>{t('stammdaten.nummer')}</span>
            <input
              type="text"
              key={blatt.code}
              defaultValue={blatt.code}
              disabled={gesperrt}
              onBlur={(e) => {
                const neu = e.target.value.trim()
                if (neu && neu !== blatt.code) {
                  void aufPflege({ felder: { code: neu } })
                }
              }}
            />
          </label>
        </p>
      )}
      {beschriftbar && (
        <Beschriftungen
          blatt={blatt}
          laeuft={gesperrt}
          abweichungen={abweichungen}
          sprachen={sprachen}
          aufPflege={aufPflege}
        />
      )}
      {!ohneEigenschaften && (
        <Eigenschaften
          blatt={blatt}
          vorrat={vorrat}
          laeuft={gesperrt}
          abweichungen={abweichungen}
          aufPflege={aufPflege}
          aufNeuLaden={aufNeuLaden}
        />
      )}
    </div>
  )
}

/**
 * *Die Objekt-ID am Blatt — sichtbar, aber nicht im Weg* (R553).
 *
 * **Sie ist nicht für den Anwender gedacht und muss trotzdem greifbar sein.**
 * Wer sie braucht, braucht sie genau einmal und vollständig: beim Einrichten
 * einer Zuordnung in einem Fremdsystem. Deshalb ein Knopf, der sie in die
 * Zwischenablage legt, und kein Feld zum Markieren — eine ID, die jemand mit
 * der Maus halb erwischt, ist genau die stille Falschauskunft, gegen die die
 * Prüfziffern gebaut sind (R551).
 *
 * **Die Rückfallkette endet weiter beim Code** (R238): Diese Zeile ist eine
 * Auskunft über das Objekt, keine Beschriftung davon. Sie steht deshalb unter
 * der Überschrift und nicht darin.
 */
function Objektkennungszeile({ kennung }: { kennung: string }) {
  const [kopiert, setKopiert] = useState(false)
  const [fehlgeschlagen, setFehlgeschlagen] = useState(false)

  // Die Rückmeldung verschwindet wieder; der Fehlschlag bleibt stehen, bis es
  // ein zweites Mal versucht wird — er ist eine Auskunft, kein Lob.
  useEffect(() => {
    if (!kopiert) return
    const uhr = window.setTimeout(() => setKopiert(false), 2000)
    return () => window.clearTimeout(uhr)
  }, [kopiert])

  // **Gibt der Browser keine Zwischenablage her, steht der Knopf nicht da.**
  // Die erste Fassung hat ihn immer gezeigt und im Fehlfall nichts getan — und
  // genau das hat die Funktionsprüfung des Hauses gemeldet: „ohne Wirkung und
  // ohne Kennzeichnung" (R451). Ein Knopf, der manchmal nichts tut, ist
  // schlimmer als keiner; die ID bleibt auch ohne ihn markierbar. Dieselbe
  // Regel wie bei fehlendem Pflegerecht: nicht ausgegraut, sondern nicht da
  // (R155).
  const gehtKopieren = typeof navigator !== 'undefined' && !!navigator.clipboard

  function kopiere() {
    navigator.clipboard
      .writeText(kennung)
      .then(() => setKopiert(true))
      .catch(() => setFehlgeschlagen(true))
  }

  return (
    <p className="objektid">
      <span className="marke-objektid">{t('stammdaten.objektid')}</span>{' '}
      <code data-kennung>{kennung}</code>{' '}
      {gehtKopieren && (
        <button type="button" className="schmal" onClick={kopiere}>
          {t('stammdaten.objektid.kopieren')}
        </button>
      )}
      <span role="status" className="objektid-gemeldet">
        {fehlgeschlagen
          ? t('stammdaten.objektid.ging.nicht')
          : kopiert
            ? t('stammdaten.objektid.kopiert')
            : ''}
      </span>
    </p>
  )
}

/**
 * Die Beschriftungen als Tabelle, nicht als Karteireiter.
 *
 * **Wer übersetzt, vergleicht** — und ein Reiter versteckt genau die Zeile,
 * gegen die man prüft. Fehlende Sprachen sind sichtbar leer, nicht abwesend.
 */
function Beschriftungen({
  blatt,
  laeuft,
  abweichungen,
  sprachen,
  aufPflege,
}: {
  blatt: Stammdatenblatt
  laeuft: boolean
  /** Was heute anders steht (R518). */
  abweichungen: Map<string, Abweichung> | null
  /** Was das Haus erklärt hat (R556). */
  sprachen: Haussprache[]
  aufPflege: (auftrag: Pflegeauftrag) => Promise<void>
}) {
  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse, rahmen: spaltenrahmen } =
    useSpaltenmasse<HTMLTableElement>('stammdaten:beschriftungen')

  /*
    **Jede erklärte Sprache hat ihre Zeile** (R696, 2026-09-22).

    Bis heute zeigte die Tabelle nur, was gepflegt war; der Rest lag hinter
    einer Auswahl *Sprache ergänzen* mit einem Knopf daneben. Das widersprach
    dem, was über dieser Tabelle seit 18.6 steht: *Fehlende Sprachen sind
    sichtbar leer, nicht abwesend.* Jetzt stimmt es.

    **Eine stillgelegte Sprache bekommt keine leere Zeile** (R316): Sie nimmt
    nichts Neues mehr an — die Datenbank weist es ab —, und ein Feld, in das
    man vergeblich tippt, ist schlimmer als keines. Was in ihr geschrieben
    wurde, steht weiter da und bleibt lesbar.
  */
  const gepflegt = new Map(blatt.sprachen.map((z) => [z.sprache, z]))
  const zeilen = [
    ...blatt.sprachen,
    ...sprachen
      .filter((s) => s.istStammdatensprache && !s.istStillgelegt && !gepflegt.has(s.code))
      .map((s) => ({ sprache: s.code, name: null, kuerzel: null })),
  ]

  return (
    <Blattabschnitt
      kennung="beschriftungen"
      titel={t('stammdaten.beschriftungen')}
      // Ohne „2 Sprachen" neben dem Aufklapper (7.2 Abschnitt 36, R1143).
    >

      <div className="tabellenrahmen">
        <table ref={spaltenrahmen} className="stammdatentexte ziehbaretabelle">
          <Spaltenmassleiste masse={spaltenmasse} />
          <thead>
            <tr>
              <th>{t('stammdaten.sprache')}</th>
              <th>{t('stammdaten.name')}</th>
              <th>{t('stammdaten.kuerzel')}</th>
            </tr>
          </thead>
          <tbody>
            {zeilen.map((z) => {
              const amNamen = abweichungen?.get(`text:${z.sprache}:name`)
              const amKuerzel = abweichungen?.get(`text:${z.sprache}:kuerzel`)

              return (
                <tr key={z.sprache}>
                  {/* Die Sprache bei ihrem Eigennamen, nicht „de" (R1450). */}
                  <td className="sprachname">
                    {sprachen.find((s) => s.code === z.sprache)?.eigenname ?? z.sprache}
                  </td>
                  {/* **Der Tooltip sitzt an der Zelle und nicht am Feld** (R518):
                      Im Alt-Stand ist jedes Feld `disabled`, und ein gesperrtes
                      Feld nimmt den Mauszeiger nicht an — der Tooltip daran
                      erschiene nie. */}
                  <td
                    className={amNamen ? 'heuteanders' : undefined}
                    title={heutesatz(amNamen)}
                  >
                    <Textfeld
                      wert={z.name}
                      laeuft={laeuft}
                      beschriftung={`${t('stammdaten.name')} (${z.sprache})`}
                      aufSpeichern={(text) =>
                        aufPflege({ texte: [{ sprache: z.sprache, textart: 'name', text }] })
                      }
                    />
                  </td>
                  <td
                    className={amKuerzel ? 'heuteanders' : undefined}
                    title={heutesatz(amKuerzel)}
                  >
                    <Textfeld
                      wert={z.kuerzel}
                      laeuft={laeuft}
                      beschriftung={`${t('stammdaten.kuerzel')} (${z.sprache})`}
                      aufSpeichern={(text) =>
                        aufPflege({ texte: [{ sprache: z.sprache, textart: 'kuerzel', text }] })
                      }
                    />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

    </Blattabschnitt>
  )
}

/**
 * Wofür ein Feld gepflegt wird (7.4 Abschnitt 23.4, R697).
 *
 * **Drei Stufen, und die erste, die etwas sagt, gewinnt** — dieselbe
 * Rückfallkette wie bei jeder Beschriftung (R238):
 *
 * 1. Der Satz, den **das Haus** gepflegt hat. Er gilt auch für die Felder, die
 *    das Haus selbst erklärt hat (R554) — für die es vom Werk keinen geben kann.
 * 2. Der Satz **des Werks je Objektart**. `art` meint an der Bewertungsebene
 *    etwas anderes als am Positionsplan; ein Satz für beide wäre für einen von
 *    beiden falsch.
 * 3. Der Satz **des Werks für dieses Feld überall** — `stillgelegt_ab` und
 *    `sortierung` bedeuten an jeder Objektart dasselbe.
 *
 * **Bleibt alles leer, steht nichts da** und kein Platzhalter: Wo sich der
 * Zweck nicht belegen ließ, ist die leere Zelle die ehrlichere Auskunft.
 */
export function zwecksatz(objektart: string, feld: Feldangabe): string {
  const hausfassung = beschriftung(feld.zweck ?? {})
  if (hausfassung) {
    return hausfassung
  }

  return textOderRoh(
    `stammdaten.zweck.${objektart}.${feld.feld}`,
    textOderRoh(`stammdaten.zweck.${feld.feld}`, ''),
  )
}

/**
 * Die Zelle „Wofür" — anzeigen, und auf Wunsch überschreiben (R697).
 *
 * **Sie steht zunächst als Satz da und nicht als Feld.** Ein Eingabefeld in
 * jeder Zeile machte aus einer Auskunft ein Formular, und der Anwender pflegt
 * hier zu 99 von 100 Malen nichts — er liest.
 *
 * **Der Stift daneben öffnet es**, und darüber steht, was der Anwender sonst
 * nicht wissen kann: **Der Satz gilt für alle Objekte dieser Art.** Wer ihn am
 * Blatt der Nordlicht Toulouse ändert, ändert ihn für alle zweihundert
 * Gesellschaften. Diese Auskunft steht dort, wo die Änderung geschieht, und
 * nicht in einer Hilfe, die niemand aufschlägt (R143, R145).
 */
function Zweckfeld({
  objektart,
  feld,
  laeuft,
  aufNeuLaden,
}: {
  objektart: string
  feld: Feldangabe
  laeuft: boolean
  aufNeuLaden: () => void
}) {
  const [offen, setOffen] = useState(false)
  const darf = useDarf()
  const satz = zwecksatz(objektart, feld)
  const eigen = beschriftung(feld.zweck ?? {})

  if (!offen) {
    return (
      <>
        <span className={eigen ? 'zwecksatz eigen' : 'zwecksatz'}>{satz}</span>
        {/* Ohne Recht steht der Satz da, der Stift nicht (R1180). */}
        {darf('zweckPflegen') && (
        <button
          type="button"
          className="schmal zweckstift"
          disabled={laeuft}
          aria-label={t('stammdaten.zweck.aendern', { feld: feld.feld })}
          onClick={() => setOffen(true)}
        >
          {t('stammdaten.zweck.stift')}
        </button>
        )}
      </>
    )
  }

  return (
    <>
      <span className="erlaeuterung">{t('stammdaten.zweck.giltfueralle')}</span>
      <Textfeld
        wert={eigen || null}
        platzhalter={satz}
        laeuft={laeuft}
        beschriftung={t('stammdaten.zweck.aendern', { feld: feld.feld })}
        aufSpeichern={async (text) => {
          await api.zweckPflegen(objektart, feld.feld, aktuelleSprache(), text)
          setOffen(false)
          aufNeuLaden()
        }}
      />
    </>
  )
}

/**
 * Die Eigenschaften — mit Wert, Herkunft und Weg (R242).
 *
 * **Beide Richtungen sind ein Handgriff:** „Überschreiben" schreibt den heute
 * geltenden Wert ausdrücklich ans Objekt, „Erben" leert das Feld und gibt es
 * an die Gruppe zurück. Ohne die Rückrichtung wäre jede versehentliche
 * Überschreibung unumkehrbar, denn eine leere Eingabe sähe aus wie ein
 * Versehen.
 */
function Eigenschaften({
  blatt,
  vorrat,
  laeuft,
  abweichungen,
  aufPflege,
  aufNeuLaden,
}: {
  blatt: Stammdatenblatt
  /** Die wählbaren Einträge je Verweisart (R327). */
  vorrat: Record<string, Listeneintrag[]>
  laeuft: boolean
  /** Was heute anders steht (R518). */
  abweichungen: Map<string, Abweichung> | null
  aufPflege: (auftrag: Pflegeauftrag) => Promise<void>
  /** Nach einem Zwecksatz: Das Blatt holt sich seine Sätze neu (R697). */
  aufNeuLaden: () => void
}) {
  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse, rahmen: spaltenrahmen } =
    useSpaltenmasse<HTMLTableElement>('stammdaten:eigenschaften')

  /*
    **Die Herkunft steht, wo geerbt wird — und sonst nicht** (R698, 2026-09-22).

    Von fünfundzwanzig Objektarten kennen genau drei eine Vererbung: Konto,
    Kontengruppe und Bewegungsart. Dort sagt die Spalte, von welcher Gruppe ein
    Wert kommt, und daran hängen die Knöpfe *Überschreiben* und *Erben*.

    **Überall sonst sagte sie „hier gesetzt" oder „nirgends gepflegt"** — und
    das sieht man dem Feld daneben an: Es ist gefüllt oder leer. Eine Spalte,
    die wiederholt, was nebenan steht, beantwortet keine Frage.
  */
  const zeigtHerkunft = blatt.felder.some((f) => f.vererbbar)
  // Ohne Recht zum Pflegen fehlt die Spalte der Griffe — Vorschlag, Überschreiben, Erben (R1180).
  const darf = useDarf()
  // Ein Konto ab Werk hat keine Griffe (R1422) — der Dienst lehnte jeden ab.
  const mitGriffen = darf('stammdatumPflegen') && !blatt.abWerk

  return (
    <Blattabschnitt
      kennung="eigenschaften"
      titel={t('stammdaten.eigenschaften')}
      // Ohne „14 Eigenschaften" neben dem Aufklapper (7.2 Abschnitt 36, R1143).
    >
      {blatt.abWerk && <p className="meldung neutral">{t('stammdaten.abwerk.hinweis')}</p>}

      {blatt.freigegebeneAbschluesse > 0 && (
        <p className="erlaeuterung">
          {t('stammdaten.rechnend.warnung', { anzahl: anzahlwort(blatt.freigegebeneAbschluesse) })}
        </p>
      )}

      <div className="tabellenrahmen">
        <table ref={spaltenrahmen} className="stammdatenfelder ziehbaretabelle">
          <Spaltenmassleiste masse={spaltenmasse} />
          <thead>
            <tr>
              <th>{t('stammdaten.feld')}</th>
              <th>{t('stammdaten.wert')}</th>
              {zeigtHerkunft && <th>{t('stammdaten.herkunft')}</th>}
              {/* **Wozu die Angabe gepflegt wird** (R697, 2026-09-22). Wer vor
                  einem Blatt mit siebzehn Eigenschaften sitzt, weiß bei der
                  Hälfte nicht, warum — und pflegt sie deshalb nicht oder
                  falsch. */}
              <th>{t('stammdaten.zweck')}</th>
              {mitGriffen && <th />}
            </tr>
          </thead>
          <tbody>
            {blatt.felder.map((f) => {
              const anders = abweichungen?.get(`feld:${f.feld}`)

              // **Ohne Zwischenzeile je Klasse** (R1457, Entscheidung vom
              // 2026-10-09). Bis dahin stand über jeder Gruppe „Rechnend",
              // „Ordnend", „Beschriftend" (R555). Die Reihenfolge bleibt: Der
              // Feldervorrat steht nach Wirkung, was rechnet, steht oben.
              return (
              <Fragment key={f.feld}>
              <tr>
                <td>
                  {/* **Ein Hausfeld trägt seine eigene Beschriftung** (R554):
                      Es entsteht beim Kunden und steht in keiner Sprachdatei.
                      Die Rückfallkette ist dieselbe wie überall (R238), und am
                      Ende steht sein Bezeichner — lesbar, nicht leer. */}
                  {f.istHausfeld
                    ? beschriftung(f.beschriftung ?? {}) || f.feld
                    : feldname(blatt.objektart, f.feld)}
                  {/* **Das Etikett „rechnend" ist gefallen** (R697,
                      2026-09-22): Der Zwecksatz rechts sagt dasselbe genauer,
                      und zwei Angaben derselben Sache in einer Zeile sind eine
                      zu viel. Die Zwischenzeilen je Klasse bleiben stehen. */}
                  {/* Warum das Feld beim Nachbarn fehlt: Ein Werksfeld hat jede
                      Anlage, ein Hausfeld hat dieses Haus. */}
                  {f.istHausfeld && <span className="merkmal hausfeld">{t('stammdaten.hausfeld')}</span>}
                </td>
                {/* Der Tooltip an der Zelle, nicht am gesperrten Feld (R518). */}
                <td className={anders ? 'heuteanders' : undefined} title={heutesatz(anders)}>
                  <Wertfeld
                    feld={f}
                    vorrat={f.verweisart ? vorrat[f.verweisart] ?? [] : []}
                    laeuft={laeuft}
                    aufPflege={aufPflege}
                  />
                </td>
                {/* Leer, wo das Feld nicht erben kann (R1451): „hier gesetzt“
                    sagte dort nur, was der Wert daneben zeigt. */}
                {zeigtHerkunft && !f.vererbbar && <td className="herkunft" />}
                {zeigtHerkunft && f.vererbbar && (
                  <td className="herkunft">
                    {f.herkunft === 'geerbt' ? (
                      <span className="geerbt">
                        {/* Mit Namen, nicht mit Kennung (R1433): Bis zum
                            2026-10-07 stand hier „geerbt von KG-AKTIVA“. */}
                        {t('stammdaten.geerbt', { von: verweisname(f.herkunftVon, vorrat) })}
                      </span>
                    ) : f.herkunft === 'gesetzt' ? (
                      <span className="gesetzt">{t('stammdaten.gesetzt')}</span>
                    ) : (
                      <span className="gedaempft">{t('stammdaten.ungepflegt')}</span>
                    )}
                  </td>
                )}
                {/* **Der Satz des Hauses gewinnt, der des Werks scheint durch**
                    (R697, R238): Wo das Haus nichts gepflegt hat, steht der
                    Werkssatz — sonst hätte ein Haus, das die Pflege halb macht,
                    eine Spalte mit Lücken. */}
                <td className="zweck">
                  <Zweckfeld
                    objektart={blatt.objektart}
                    feld={f}
                    laeuft={laeuft}
                    aufNeuLaden={aufNeuLaden}
                  />
                </td>
                {mitGriffen && (
                <td className="handgriff">
                  {/* **Ein Griff, kein zweiter Bildschirm** (R143). Der
                      Vorschlag wirkt erst, wenn der Anwender ihn nimmt (R5) —
                      und was er nicht nimmt, bleibt leer, statt sich still
                      einzutragen. */}
                  {f.vorschlag && f.eigen !== f.vorschlag && (
                    <button
                      type="button"
                      className="schmal"
                      disabled={laeuft}
                      /*
                        **Der Grund steht nicht mehr auf dem Bildschirm**
                        (Entscheidung vom 2026-09-22, R698). Er stand bis
                        dahin als eigener Satz unter der Herkunft — und die
                        Spalte ist gefallen.

                        **Er ist damit nicht fort, sondern abrufbar:** Wer
                        wissen will, warum das System *voll* vorschlägt, hält
                        den Zeiger über den Knopf. Das kostet keinen
                        Bildschirmplatz und nimmt die Auskunft niemandem weg,
                        der sie sucht.
                      */
                      /*
                        **Die Stimmen stehen im Zahlenformat des Anwenders**
                        (R840): Der Dienst liefert sie als „33.3333", und so
                        standen sie bis zum 2026-09-30 im Satz — „mit
                        33.3333 % die Stimmenmehrheit".
                      */
                      title={
                        f.vorschlagsgrund
                          ? vorschlagssatz(f.vorschlagsgrund, f.vorschlagswerte)
                          : undefined
                      }
                      onClick={() => void aufPflege({ felder: { [f.feld]: f.vorschlag } })}
                    >
                      {/* Auch auf dem Knopf steht die Zahl wie im Feld daneben (R840). */}
                      {t('stammdaten.vorschlag.uebernehmen', {
                        wert: wertanzeige(f, f.vorschlag),
                      })}
                    </button>
                  )}
                  {f.vererbbar && f.herkunft === 'geerbt' && (
                    <button
                      type="button"
                      className="schmal"
                      disabled={laeuft}
                      onClick={() => aufPflege({ felder: { [f.feld]: f.wert } })}
                    >
                      {t('stammdaten.ueberschreiben')}
                    </button>
                  )}
                  {f.vererbbar && f.herkunft === 'gesetzt' && (
                    <button
                      type="button"
                      className="schmal"
                      disabled={laeuft}
                      onClick={() => aufPflege({ felder: { [f.feld]: null } })}
                    >
                      {t('stammdaten.erben')}
                    </button>
                  )}
                </td>
                )}
              </tr>
              </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>

      {blatt.felder.length === 0 && <p className="hinweis">{t('stammdaten.keinefelder')}</p>}
    </Blattabschnitt>
  )
}

/**
 * Das Bedienelement zu einem Feld — nach seiner Art, nicht nach seiner
 * Bedeutung.
 *
 * Ein geerbtes Feld wird **gedämpft** dargestellt und trägt seinen geerbten
 * Wert: Wer hineinschreibt, überschreibt — und das ist genau die Handlung, die
 * daneben als „überschreiben" steht.
 */
function Wertfeld({
  feld,
  vorrat,
  laeuft,
  aufPflege,
}: {
  feld: Feldangabe
  vorrat: Listeneintrag[]
  laeuft: boolean
  aufPflege: (auftrag: Pflegeauftrag) => Promise<void>
}) {
  const geerbt = feld.herkunft === 'geerbt'
  const klasse = geerbt ? 'geerbtes' : ''
  // Der geerbte Wert eines Verweises steht mit Namen in Klammern (R1433).
  const geerbterVerweis = vorrat.find((e) => e.schluessel === feld.wert)
  const geerbterName = geerbterVerweis ? katalogbezeichnung(geerbterVerweis) : ''

  // **Wo das Feld nichts anbieten darf, steht der Grund** (R1430) — ein
  // fehlendes Feld hielte man sonst für einen Fehler.
  if (feld.sperre) {
    const namen = Object.fromEntries(
      Object.entries(feld.sperrnamen ?? {}).map(([k, v]) => [k, beschriftung(v)]),
    )
    return (
      <span className="sperrsatz">
        {t(`stammdaten.sperre.${feld.feld}.${feld.sperre}` as Sprachschluessel, namen)}
      </span>
    )
  }

  // **Das Saldowechselziel wird im Baum von Bilanz und GuV gewählt** (2.7
  // Abschnitt 3.7a, R1432) — angeboten wird nur, was der Dienst zulässt.
  if (feld.feld === 'saldowechsel_ziel' && vorrat.length > 0) {
    const zulaessig = new Set(feld.vorrat)
    return (
      <Kontofeld
        waehlt="position"
        className={klasse}
        wert={feld.eigen ?? ''}
        eintraege={vorrat.filter((e) => zulaessig.has(e.schluessel)).map(ausStammdatum)}
        abgeschaltet={laeuft}
        beschriftung={t('stammdaten.feld.saldowechsel_ziel')}
        leer={geerbt ? `(${geerbterName})` : '—'}
        aufWahl={(gewaehlt) => void aufPflege({ felder: { [feld.feld]: gewaehlt || null } })}
      />
    )
  }

  // Ein Verweis wird ausgesucht (R327). Solange die Liste noch nicht da ist,
  // bleibt es beim Textfeld darunter: ein leeres Auswahlfeld sähe aus, als
  // gäbe es nichts zu wählen.
  if (feld.verweisart && vorrat.length > 0) {
    return (
      <Auswahlfeld
        className={klasse}
        wert={feld.eigen ?? ''}
        abgeschaltet={laeuft}
        beschriftung={t(`stammdaten.feld.${feld.feld}` as Sprachschluessel)}
        leer={geerbt ? `(${geerbterName})` : '—'}
        eintraege={vorrat.map(ausStammdatum)}
        aufWahl={(gewaehlt) => void aufPflege({ felder: { [feld.feld]: gewaehlt || null } })}
      />
    )
  }

  if (feld.art === 'jaNein') {
    return (
      <select
        className={klasse}
        value={feld.eigen ?? ''}
        disabled={laeuft}
        aria-label={t(`stammdaten.feld.${feld.feld}` as Sprachschluessel)}
        onChange={(e) => aufPflege({ felder: { [feld.feld]: e.target.value || null } })}
      >
        <option value="">{geerbt ? `(${feld.wert === 'true' ? t('ja') : t('nein')})` : '—'}</option>
        <option value="true">{t('ja')}</option>
        <option value="false">{t('nein')}</option>
      </select>
    )
  }

  if (feld.art === 'auswahl' && feld.vorrat.length > 0) {
    return (
      /*
        **Eine Aufzählung sucht erst, wenn sie länger ist als zwölf** (R583).

        Der Vorrat kommt aus den Prüfregeln der Datenbank (R543) und ist heute
        immer kurz — die längste Aufzählung hat vier Einträge (*operativ ·
        holding · zweckgesellschaft · ruhend*). Hier steht deshalb **keine
        Umstellung, sondern eine Vorsorge**: Entsteht eines Tages ein langer
        Vorrat, entscheidet das Feld es selbst, und an dieser Stelle ist nichts
        nachzuziehen.
      */
      <Auswahlfeld
        sorte="aufzaehlung"
        className={klasse}
        wert={feld.eigen ?? ''}
        abgeschaltet={laeuft}
        beschriftung={t(`stammdaten.feld.${feld.feld}` as Sprachschluessel)}
        leer={geerbt ? `(${wertwort(feld.feld, feld.wert)})` : '—'}
        eintraege={feld.vorrat.map((v) => ({ wert: v, text: wertwort(feld.feld, v), suchtext: v }))}
        aufWahl={(gewaehlt) => void aufPflege({ felder: { [feld.feld]: gewaehlt || null } })}
      />
    )
  }

  // **Eine Periode wird im Periodenfeld getippt** (R839 — Eine Periode liest
  // sich als Monat und Jahr). Bis zum 2026-09-30 fiel die Feldart auf das
  // freie Textfeld darunter und zeigte dort die Speicherform: „Gültig ab
  // Periode 202603".
  if (feld.art === 'periode') {
    return (
      <Periodenwertfeld
        wert={feld.eigen}
        geerbt={geerbt ? feld.wert : null}
        laeuft={laeuft}
        beschriftung={t(`stammdaten.feld.${feld.feld}` as Sprachschluessel)}
        aufSpeichern={(periode) => aufPflege({ felder: { [feld.feld]: periode } })}
      />
    )
  }

  // **Eine Zahl wird im Zahlenfeld getippt** (R840 — Eine Zahl in den
  // Stammdaten steht wie im Bericht). Bis zum 2026-09-30 fiel sie auf das
  // freie Textfeld darunter und zeigte dort die Speicherform: „0.80000000"
  // beim Kapitalanteil, „1600000.00" bei den Anschaffungskosten.
  //
  // **Ganz ist nur die Zahl des Werks** — Sortierung, Nutzungsdauer in
  // Monaten. Die Zahl eines Hausfelds darf Nachkommastellen tragen; der Dienst
  // liest sie als Kommazahl.
  if (istZahlart(feld.art)) {
    return (
      <Zahlwertfeld
        wert={feld.eigen}
        geerbt={geerbt ? feld.wert : null}
        darstellung={feld.darstellung ?? null}
        ganz={feld.art === 'zahl' && !feld.istHausfeld}
        laeuft={laeuft}
        beschriftung={
          feld.istHausfeld
            ? beschriftung(feld.beschriftung ?? {}) || feld.feld
            : t(`stammdaten.feld.${feld.feld}` as Sprachschluessel)
        }
        aufSpeichern={(zahl) => aufPflege({ felder: { [feld.feld]: zahl } })}
      />
    )
  }

  return (
    <Textfeld
      wert={feld.eigen}
      platzhalter={geerbt ? (feld.wert ?? '') : undefined}
      klasse={klasse}
      laeuft={laeuft}
      beschriftung={t(`stammdaten.feld.${feld.feld}` as Sprachschluessel)}
      aufSpeichern={(text) => aufPflege({ felder: { [feld.feld]: text || null } })}
    />
  )
}

/**
 * Eine Periode im Blatt — im selben Feld wie überall (R839 — Eine Periode
 * liest sich als Monat und Jahr; R502 — Am Bildschirm steht MM.JJJJ).
 *
 * **Gespeichert wird beim Verlassen, wie im Textfeld daneben** (R241). Das
 * Periodenfeld meldet bei jedem Tastendruck, und `2026-1` ist auf dem Weg zu
 * `2026-12` schon der Januar — das wäre eine Fassung, die niemand wollte.
 * Gelesen wird deshalb, was beim Verlassen im Feld steht; die Eingabetaste tut
 * dasselbe.
 *
 * **Leer heißt: die Angabe fällt weg. Unlesbar heißt: es wird nichts
 * gespeichert** — den Satz dazu schreibt das Feld selbst unter sich. Auf die
 * Reise geht die Speicherform `jjjjmm`, in welcher Schreibweise auch getippt
 * wurde.
 *
 * **Ohne eigene Beschriftung** (`nurfeld`): Die Zeile der Tabelle nennt das
 * Feld schon in ihrer ersten Zelle.
 */
function Periodenwertfeld({
  wert,
  geerbt,
  laeuft,
  beschriftung: name,
  aufSpeichern,
}: {
  /** Der eigene Wert in der Speicherform `jjjjmm` — `null`, wo keiner gesetzt ist. */
  wert: string | null
  /** Der geerbte Wert, wo das Feld erbt — er steht dann unter dem Feld. */
  geerbt: string | null
  laeuft: boolean
  beschriftung: string
  aufSpeichern: (periode: string | null) => Promise<void>
}) {
  const zahl = Number(wert)
  const bestand = wert && Number.isInteger(zahl) ? zahl : null

  const speichern = (text: string | null) => {
    if (text === null) {
      return
    }

    if (text.trim() === '') {
      if (bestand !== null) {
        void aufSpeichern(null)
      }

      return
    }

    const gelesen = ausPeriodenfeld(text)
    if (gelesen !== null && gelesen !== bestand) {
      void aufSpeichern(String(gelesen))
    }
  }

  return (
    <span
      className="periodenwert"
      onBlur={(e) => speichern(feldtext(e.target))}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          speichern(feldtext(e.target))
        }
      }}
    >
      <Periodenfeld
        wert={bestand}
        // Gespeichert wird beim Verlassen, nicht bei jedem Tastendruck — siehe oben.
        aufWert={() => {}}
        beschriftung={name}
        klasse="nurfeld"
        gesperrt={laeuft}
        erlaeuterung={geerbt ? `(${periodenwort(geerbt)})` : undefined}
      />
    </span>
  )
}

/**
 * Eine Zahl im Blatt — im selben Feld wie überall (R840 — Eine Zahl in den
 * Stammdaten steht wie im Bericht).
 *
 * **Gespeichert wird beim Verlassen, wie bei der Periode darüber** (R241): Das
 * Zahlenfeld meldet bei jedem Tastendruck, und „1" ist auf dem Weg zu
 * „1.600.000" schon eine Zahl — das wäre eine Fassung, die niemand wollte.
 * Gelesen wird deshalb, was beim Verlassen im Feld steht; die Eingabetaste tut
 * dasselbe.
 *
 * **Leer heißt: die Angabe fällt weg. Unlesbar heißt: es wird nichts
 * gespeichert** — den Satz dazu schreibt das Feld selbst unter sich. Auf die
 * Reise geht die Speicherform: `1600000` für „1.600.000,00", und beim Anteil
 * der Bruch — `0.805` für „80,5".
 *
 * **Dieselbe Zahl in anderer Schreibweise ist keine Änderung.** Im Bestand
 * steht `0.80000000`, im Feld „80": Wer das Feld nur betritt und verlässt,
 * schreibt nichts.
 *
 * **Ohne eigene Beschriftung** (`nurfeld`): Die Zeile der Tabelle nennt das
 * Feld schon in ihrer ersten Zelle.
 */
function Zahlwertfeld({
  wert,
  geerbt,
  darstellung,
  ganz,
  laeuft,
  beschriftung: name,
  aufSpeichern,
}: {
  /** Der eigene Wert in der Speicherform — `null`, wo keiner gesetzt ist. */
  wert: string | null
  /** Der geerbte Wert, wo das Feld erbt — er steht dann unter dem Feld. */
  geerbt: string | null
  /** Anteil, Betrag oder Kurs — `null` bei der Zahl ohne Darstellung. */
  darstellung: Zahlendarstellung | null
  /** Ob das Feld nur ganze Zahlen führt. */
  ganz: boolean
  laeuft: boolean
  beschriftung: string
  aufSpeichern: (zahl: string | null) => Promise<void>
}) {
  // Eine Kommazahl, wo nur eine ganze Zahl stehen darf: Das Zahlenfeld liest
  // sie, deshalb sagt es die Hülle — als Satz unter dem Feld (R145).
  const [nichtGanz, setNichtGanz] = useState(false)

  const speichern = (text: string | null) => {
    if (text === null) {
      return
    }

    if (text.trim() === '') {
      setNichtGanz(false)
      if (wert) {
        void aufSpeichern(null)
      }

      return
    }

    const gelesen = liesFeldzahl(text, darstellung, ganz)
    setNichtGanz(gelesen === null && ganz && liesFeldzahl(text, darstellung, false) !== null)

    // Verglichen wird über die Speicherform ohne Endnullen: `0.8` gegen
    // `0.80000000` ist keine Änderung.
    if (gelesen !== null && speicherform(gelesen) !== speicherform(wert)) {
      void aufSpeichern(gelesen)
    }
  }

  return (
    <span
      className="zahlenwert"
      onBlur={(e) => speichern(feldtext(e.target))}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          speichern(feldtext(e.target))
        }
      }}
    >
      <Zahlenfeld
        wert={wert}
        // Gespeichert wird beim Verlassen, nicht bei jedem Tastendruck — siehe oben.
        aufWert={() => {}}
        darstellung={darstellung}
        beschriftung={name}
        klasse="nurfeld"
        gesperrt={laeuft}
        erlaeuterung={
          nichtGanz
            ? t('stammdaten.zahl.nurganz')
            : geerbt
              ? `(${zahlenwort(geerbt, darstellung)})`
              : undefined
        }
      />
    </span>
  )
}

/** Der Text des Eingabefelds, von dem ein Ereignis kommt — `null`, wenn es keines ist. */
function feldtext(ziel: EventTarget): string | null {
  return ziel instanceof HTMLInputElement ? ziel.value : null
}

/**
 * Ein Textfeld, das beim Verlassen speichert.
 *
 * **Nicht bei jedem Tastendruck:** Jede Änderung ist eine neue Fassung (R241),
 * und eine Fassung je Buchstabe machte die Fassungsreihe unlesbar. Die
 * Eingabetaste tut dasselbe wie das Verlassen — wer tippt, hebt die Hände
 * nicht.
 */
function Textfeld({
  wert,
  platzhalter,
  klasse,
  laeuft,
  beschriftung: name,
  aufSpeichern,
}: {
  wert: string | null
  platzhalter?: string
  klasse?: string
  laeuft: boolean
  beschriftung: string
  aufSpeichern: (text: string) => Promise<void>
}) {
  const [entwurf, setEntwurf] = useState(wert ?? '')

  useEffect(() => setEntwurf(wert ?? ''), [wert])

  const speichern = () => {
    if (entwurf !== (wert ?? '')) {
      void aufSpeichern(entwurf)
    }
  }

  return (
    <input
      className={klasse}
      value={entwurf}
      placeholder={platzhalter}
      disabled={laeuft}
      aria-label={name}
      onChange={(e) => setEntwurf(e.target.value)}
      onBlur={speichern}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          speichern()
        }
      }}
    />
  )
}

/* --------------------------------------------------------------------------
   Rechts: Herkunft und Fassungsreihe
   -------------------------------------------------------------------------- */

/**
 * Die Fassungsreihe (R241).
 *
 * **Sie steht rechts und nicht als Fußnote am Feld.** Ein Feld mit vier
 * Zusätzen ist unlesbar; eine Spalte mit vier Zeilen ist eine Liste.
 */
function Herkunft({
  reihe,
  blick,
  aufBlick,
  eingeklappt,
  aufKlappen,
}: {
  /** Die **vollständige** Reihe — nicht die des betrachteten Standes (R519). */
  reihe: Fassungsangabe[]
  /** Der betrachtete Stand, `null` für heute (R517). */
  blick: number | null
  aufBlick: (stand: number | null) => void
  /** Ob die Spalte auf ihre Symbolbreite zusammengelegt ist. */
  eingeklappt: boolean
  aufKlappen: (eingeklappt: boolean) => void
}) {
  /*
    **Die jüngste Fassung IST der heutige Stand** (R520) und steht deshalb
    nicht ein zweites Mal darunter. Zwei Zeilen, die zu demselben Blatt führen,
    sind ein Ziel zuviel — und die obere sagte „Heute", während die untere
    dasselbe zeigte und beim Anspringen *Inhaltlich unverändert bis heute*
    meldete. Die Auskunft geht nicht verloren: Zeitpunkt, Anwender und die
    geänderten Felder stehen jetzt in der Heute-Zeile, die Fassungsnummer in
    ihrem Namen.
  */
  const [juengste, ...aeltere] = reihe

  // Was nach dem betrachteten Stand entstanden ist. Im heutigen Blick ist das
  // nichts — dort liegt nichts hinter dem Heute.
  const spaeter = blick === null ? 0 : aeltere.filter((f) => f.erfassungNr > blick).length

  return (
    <aside className={`stammdatenherkunft ${eingeklappt ? 'eng' : ''}`.trim()}>
      {/*
        **Der Kopf trägt den Klappknopf** — dieselbe Bedienung wie an der
        Bereichsleiste (R150): Überschrift links, Symbol rechts, und
        eingeklappt bleibt nur das Symbol stehen.
      */}
      <div className="herkunftskopf">
        {!eingeklappt && <h3>{t('stammdaten.fassungen')}</h3>}

        <button
          type="button"
          className="klappknopf"
          onClick={() => aufKlappen(!eingeklappt)}
          aria-label={
            eingeklappt ? t('stammdaten.fassungen.ausklappen') : t('stammdaten.fassungen.einklappen')
          }
          aria-expanded={!eingeklappt}
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="4" width="18" height="16" rx="2" />
            <path d="M15 4v16" />
            {eingeklappt ? <path d="M10.5 9.5L8 12l2.5 2.5" /> : <path d="M7.5 9.5l2.5 2.5-2.5 2.5" />}
          </svg>
          <span className="hinweisfahne" role="presentation">
            {t('stammdaten.fassungen')}
          </span>
        </button>
      </div>

      {eingeklappt ? null : (
        <ul className="fassungsreihe">
        {/*
          **Heute steht in der Reihe und nicht nur im Merkzeichen** (R517).
          Der Weg zurück gehört dorthin, wo der Weg hinaus war: Wer die Reihe
          zum Springen benutzt, sucht das Heute in derselben Reihe — und nicht
          in einem Band, das erst erscheint, wenn er schon gesprungen ist.

          **Es ist zugleich die einzige Rückkehr nach vorn.** Im Blick auf eine
          alte Fassung endet die Reihe dort (R517); was danach kam, zählt eine
          Vergangenheit nicht auf. Von heute aus steht wieder alles da.
        */}
        <li>
          <button
            type="button"
            className="fassung heute"
            aria-current={blick === null ? 'true' : undefined}
            // Der Name nennt den Zeitpunkt und keine Fassungsnummer (7.2
            // Abschnitt 36, R1141).
            aria-label={
              juengste
                ? t('stammdaten.fassung.heute.name', { zeit: zeitstempel(juengste.erfasstAm) })
                : t('stammdaten.fassung.heute')
            }
            onClick={() => aufBlick(null)}
          >
            <span className="nummer">{t('stammdaten.fassung.heute')}</span>
            {juengste ? (
              <>
                <span className="zeit">{zeitstempel(juengste.erfasstAm)}</span>
                <span className="wer">{anwendername(juengste.erfasstVon)}</span>
                {juengste.geaendert.length > 0 && (
                  <span className="geaendert">
                    {juengste.geaendert
                      .map((feld) => t(`stammdaten.feld.${feld}` as Sprachschluessel))
                      .join(', ')}
                  </span>
                )}
              </>
            ) : (
              <span className="zeit">{t('stammdaten.fassung.heute.zusatz')}</span>
            )}
          </button>
        </li>

        {/*
          **Was danach kam, wird gekennzeichnet und nicht entfernt** (R519,
          R143). Eine Überschrift statt sechs Einzelmarken: Die Reihe ist nach
          Nummern geordnet, also liegt alles Spätere zusammen am Kopf, und ein
          Wort darüber sagt es einmal für alle.
        */}
        {spaeter > 0 && (
          <li className="reihentrenner" aria-hidden="true">
            {/* Ohne Zahl (7.2 Abschnitt 36, R1143): Wie viele es sind, zeigt
                die Reihe darunter. */}
            {t('stammdaten.fassung.danach')}
          </li>
        )}

        {aeltere.map((f) => (
          <li key={f.erfassungNr}>
            <button
              type="button"
              className={`fassung ${blick !== null && f.erfassungNr > blick ? 'spaeter' : ''}`.trim()}
              aria-current={blick === f.erfassungNr ? 'true' : undefined}
              // Der Knopf trägt vier Zeilen; sein Name muss in einem Satz
              // sagen, wohin er führt — sonst liest ein Screenreader
              // „1590 09.09.2026 system Nummer" und nennt das einen Knopf.
              aria-label={t('stammdaten.fassung.ansehen', { zeit: zeitstempel(f.erfasstAm) })}
              onClick={() => aufBlick(f.erfassungNr)}
            >
              {/* Der Stand heißt nach seinem Zeitpunkt, nicht nach seiner Nummer (R1141). */}
              <span className="zeit">{zeitstempel(f.erfasstAm)}</span>
              <span className="wer">{anwendername(f.erfasstVon)}</span>
              {f.geaendert.length > 0 && (
                <span className="geaendert">
                  {f.geaendert
                    .map((feld) => t(`stammdaten.feld.${feld}` as Sprachschluessel))
                    .join(', ')}
                </span>
              )}
              {f.istGeloescht && <span className="geloescht">{t('stammdaten.geloescht')}</span>}
            </button>
          </li>
        ))}
        </ul>
      )}
    </aside>
  )
}

/**
 * Das Merkzeichen über einem Blatt, das nicht das heutige ist (R517).
 *
 * **Es trägt die Form des Freigabebandes** und keine eigene: Beides ist ein
 * Zustand, der über dem Inhalt steht und einen Ausweg anbietet — dieselbe
 * Umrandung mit farbiger Kante links, derselbe Aufbau aus Aussage links und
 * Knopf rechts (R515). Neu ist hier nur die Farbe der Kante.
 *
 * **Der Satz nennt den Zeitpunkt, auf die Sekunde** (7.2 Abschnitt 36,
 * R1141 — Entscheidung vom 2026-10-06). Bis dahin stand die Erfassungsnummer
 * daneben; sie war die technische Kennung des Standes und beantwortete keine
 * Frage, die der Zeitpunkt nicht besser beantwortet.
 */
function Standblick({
  blatt,
  abweichungen,
  aufHeute,
}: {
  blatt: Stammdatenblatt
  /** Was heute anders steht (R518) — `null`, solange der Vergleich unterwegs ist. */
  abweichungen: Map<string, Abweichung> | null
  aufHeute: () => void
}) {
  // Die oberste Fassung der (am Stand abgeschnittenen) Reihe ist die, nach der
  // dieses Blatt dasteht. Fehlt sie, ist der Stand älter als die erste Fassung
  // des Objekts — dann sagt der Satz genau das, ohne Nummer (R1141).
  const fassung = blatt.fassungen[0]

  return (
    <div className="standblick" role="status">
      <div className="zeile">
        <span>
          <span className="ampel warnend" />
          <strong>{t('stammdaten.blick.marke')}</strong>{' '}
          {fassung
            ? t('stammdaten.blick.satz', { zeit: zeitstempel(fassung.erfasstAm) })
            : t('stammdaten.blick.satz.ohne')}
        </span>

        <button type="button" onClick={aufHeute}>
          {t('stammdaten.blick.heute')}
        </button>
      </div>

      {/*
        **Die Farbe allein trägt es nicht** (R143). Die gefärbten Zellen sagen
        WO etwas anders ist und der Tooltip WAS — aber beides erst, wenn man
        hinsieht oder hinfährt. Ein Blatt mit dreizehn Eigenschaften hat einen
        Anwender, der nicht dreizehnmal hinfährt. Deshalb steht hier in Worten,
        welche Stellen es sind; danach weiß er, ob sich das Suchen lohnt.

        **Und wenn nichts abweicht, steht das auch da.** „Keine Abweichung" ist
        die Auskunft, die am häufigsten gesucht und am seltensten geschrieben
        wird — ein Blatt ohne Färbung sieht sonst aus wie eines, dessen
        Vergleich noch läuft.
      */}
      {abweichungen !== null && (
        <p className={`abweichungsliste ${abweichungen.size === 0 ? 'gleich' : ''}`.trim()}>
          {abweichungen.size === 0
            ? t('stammdaten.blick.gleich')
            : t('stammdaten.blick.anders', {
                stellen: [...abweichungen.values()].map((a) => a.benennung).join(' · '),
              })}
        </p>
      )}

      <p className="erlaeuterung">{t('stammdaten.blick.erlaeuterung')}</p>
    </div>
  )
}

/* --------------------------------------------------------------------------
   Der Vergleich mit heute (R518)
   -------------------------------------------------------------------------- */

/** Was an einer Stelle heute anders steht als am betrachteten Stand (R518). */
export type Abweichung = {
  /** Wie die Stelle heißt — „Nummer", „Bezeichnung (de)". Für das Band. */
  benennung: string
  /** Was heute dort steht, lesbar. Für den Tooltip. */
  heute: string
}

/**
 * Die Stellen, an denen sich der betrachtete Stand von heute unterscheidet.
 *
 * **Verglichen wird der geltende Wert, nicht der eigene.** Ein Feld, das damals
 * geerbt war und heute ausdrücklich auf denselben Wert gesetzt ist, zeigt dem
 * Anwender dasselbe — und ihm eine Abweichung zu melden, hieße, ihn auf eine
 * Verschiebung zu stoßen, die er im Blatt nirgends sieht. Die Herkunftsspalte
 * daneben sagt es ohnehin.
 *
 * **Ohne heutiges Blatt gibt es keine Abweichung, nicht etwa alle.** Solange
 * der zweite Ruf unterwegs ist, steht das Blatt ungefärbt da; eine Färbung,
 * die eine Sekunde später verschwindet, wäre schlimmer als eine, die eine
 * Sekunde später kommt.
 *
 * Die Schlüssel der Karte sind dieselben, mit denen die Zellen sie nachschlagen:
 * `code`, `feld:<name>`, `text:<sprache>:name`, `text:<sprache>:kuerzel`.
 */
function unterschiede(
  damals: Stammdatenblatt,
  heute: Stammdatenblatt | null,
  vorrat: Record<string, Listeneintrag[]>,
): Map<string, Abweichung> {
  const karte = new Map<string, Abweichung>()
  if (heute === null) {
    return karte
  }

  const merke = (schluessel: string, benennung: string, damalsWert: string, heuteWert: string) => {
    if (damalsWert !== heuteWert) {
      karte.set(schluessel, { benennung, heute: heuteWert })
    }
  }

  // Die Nummer steht in der Überschrift und nicht in der Tabelle — sie ist
  // trotzdem die Änderung, die am häufigsten vorkommt (Kontenrahmenumstellung,
  // R503) und am ehesten auffällt.
  merke(
    'code',
    t(`stammdaten.feld.${damals.codefeld}` as Sprachschluessel),
    damals.code,
    heute.code,
  )

  for (const feld of damals.felder) {
    const jetzt = heute.felder.find((x) => x.feld === feld.feld)
    if (!jetzt) {
      // Der Feldervorrat ist fest (R235); ein Feld, das es heute nicht gibt,
      // gäbe es auch damals nicht. Kein Befund, nur kein Vergleich.
      continue
    }

    merke(
      `feld:${feld.feld}`,
      t(`stammdaten.feld.${feld.feld}` as Sprachschluessel),
      lesbarerWert(feld, vorrat),
      lesbarerWert(jetzt, vorrat),
    )
  }

  // **Über die Vereinigung beider Seiten** und nicht über eine von ihnen: Eine
  // Sprache, die es damals gab und heute nicht mehr, ist genauso ein
  // Unterschied wie eine, die dazugekommen ist.
  const sprachen = [
    ...new Set([...damals.sprachen.map((z) => z.sprache), ...heute.sprachen.map((z) => z.sprache)]),
  ]

  for (const sprache of sprachen) {
    const a = damals.sprachen.find((z) => z.sprache === sprache)
    const b = heute.sprachen.find((z) => z.sprache === sprache)

    merke(
      `text:${sprache}:name`,
      `${t('stammdaten.name')} (${sprache.toUpperCase()})`,
      a?.name ?? '',
      b?.name ?? '',
    )
    merke(
      `text:${sprache}:kuerzel`,
      `${t('stammdaten.kuerzel')} (${sprache.toUpperCase()})`,
      a?.kuerzel ?? '',
      b?.kuerzel ?? '',
    )
  }

  return karte
}

/**
 * Ein Feldwert so, wie ihn der Anwender im Blatt liest (R518).
 *
 * Der rohe Wert taugt für den Vergleich nicht und für den Tooltip erst recht
 * nicht: Ein Verweis steht als `KG-AV-SACH` in den Daten und als
 * *AV-SACH — Sachanlagen* im Feld, ein Ja/Nein als `true`.
 */
/**
 * Der Name eines Stammdatums aus den geladenen Auswahllisten — für die
 * Herkunft *geerbt von …* (R1433).
 *
 * Bis zum 2026-10-07 stand dort die Kennung: *geerbt von KG-AKTIVA*. Solange
 * die Liste noch unterwegs ist, bleibt der Name leer statt roh.
 */
function verweisname(schluessel: string | null, vorrat: Record<string, Listeneintrag[]>): string {
  if (schluessel === null) {
    return ''
  }

  for (const liste of Object.values(vorrat)) {
    const eintrag = liste.find((e) => e.schluessel === schluessel)
    if (eintrag) {
      return katalogbezeichnung(eintrag)
    }
  }
  return ''
}

function lesbarerWert(feld: Feldangabe, vorrat: Record<string, Listeneintrag[]>): string {
  if (feld.wert === null) {
    return '—'
  }

  if (feld.verweisart) {
    const eintrag = (vorrat[feld.verweisart] ?? []).find((e) => e.schluessel === feld.wert)
    return eintrag ? katalogbezeichnung(eintrag) : feld.wert
  }

  if (feld.art === 'jaNein') {
    return feld.wert === 'true' ? t('ja') : t('nein')
  }

  // Im Tooltip und im Fassungsvergleich steht der Monat, nicht `202603` (R839)
  // — und die Zahl wie im Feld, nicht `0.80000000` (R840). Verglichen wird
  // damit zugleich, was der Anwender liest: `0.8` und `0.80000000` sind
  // derselbe Anteil und kein Unterschied zwischen zwei Fassungen.
  return wertanzeige(feld, feld.wert)
}

/** Der Tooltip an einer abweichenden Stelle — leer, wo nichts abweicht. */
function heutesatz(abweichung: Abweichung | undefined): string | undefined {
  return abweichung ? t('stammdaten.heute.wert', { wert: abweichung.heute || '—' }) : undefined
}

/* -------------------------------------------------------------------------- */

/** Die Namen einer Blattzeile als Beschriftung — damit `beschriftung()` greift. */
function namenAus(sprachen: Sprachzeile[]): Record<string, string> {
  const namen: Record<string, string> = {}
  for (const z of sprachen) {
    if (z.name) {
      namen[z.sprache] = z.name
    }
  }

  return namen
}

/**
 * Eine gespeicherte Periode als Monat und Jahr — `202603` wird „März 2026"
 * (R839 — Eine Periode liest sich als Monat und Jahr).
 *
 * **Eine Stelle für Blatt, Tafel, Seitenliste und Meldung.** Die Stammdaten
 * führen ihre Werte als Text, auch die Periode; hier wird daraus, was der
 * Anwender liest. Die rohe Zahl und die Feldform `03.2026` stehen in keiner
 * Zelle und in keinem Satz.
 *
 * **Was keine Periode ist, bleibt stehen, wie es ist.** Eine Angabe, die als
 * Periode gemeint war und keine ist, soll als das dastehen, was sie ist —
 * sonst sucht jemand den Fehler in der Anzeige statt im Wert.
 */
export function periodenwort(wert: string | number | null | undefined): string {
  if (wert === null || wert === undefined || wert === '') {
    return ''
  }

  const zahl = Number(wert)
  const monat = zahl % 100
  if (!Number.isInteger(zahl) || zahl < 100 || monat < 1 || monat > 12) {
    return String(wert)
  }

  return periodenname(zahl)
}

/** Ob die Feldart eine Zahl ist — die ganze (`zahl`) oder die Kommazahl (`dezimal`). */
function istZahlart(art: string): boolean {
  return art === 'dezimal' || art === 'zahl'
}

/**
 * Eine getippte Zahl in der Speicherform — `null`, wo sie sich nicht lesen
 * lässt (R840 — Eine Zahl in den Stammdaten steht wie im Bericht).
 *
 * **Eine Stelle für Blatt, Tafel und Anlageformular.** Gelesen wird nach dem
 * Zahlenformat des Anwenders, mit derselben Lesung wie im Zahlenfeld:
 * „1.600.000,00" und „1600000" sind derselbe Betrag. **Ein Anteil wird in
 * Prozent getippt und als Bruch gespeichert** — „80", „80 %" und „80,5"
 * werden `0.8`, `0.8` und `0.805`.
 *
 * **Eine ganze Zahl bleibt ganz.** Sortierung und Nutzungsdauer tragen keine
 * Nachkommastellen: „12,00" ist 12 und geht als `12` auf die Reise — die
 * Speicherform führt keine Endnullen —, „12,5" ist dort keine Zahl.
 */
export function liesFeldzahl(
  text: string,
  darstellung: Zahlendarstellung | null | undefined,
  ganz: boolean,
): string | null {
  const gelesen = darstellung === 'anteil' ? liesAnteil(text) : liesDezimal(text)

  // Ohne Endnullen trägt die Speicherform genau dann einen Punkt, wenn die
  // Zahl nicht ganz ist.
  return gelesen !== null && ganz && gelesen.includes('.') ? null : gelesen
}

/**
 * Ein Feldwert als das, was der Anwender liest — nach der Art des Feldes.
 *
 * **Eine Stelle für Knopf, Tooltip, Fassungsvergleich und Anlageformular:**
 * Die Periode steht als Monat und Jahr (R839), die Zahl wie im Bericht (R840
 * — „80 %" statt `0.80000000`, „1.600.000,00" statt `1600000.00`), die
 * Auswahl als ihr Wort, Ja/Nein als *Ja* und *Nein* und ein Verweis mit dem
 * Namen seines Ziels (R838), soweit der Katalog ihn kennt.
 *
 * **Auch für die Vorschau der Excel-Einfuhr** (seit dem 2026-09-30): Dort
 * stand eine Änderung als „quote: 0.80000000 → 0.75".
 */
export function wertanzeige(
  feld: { feld: string; art: string; darstellung?: Zahlendarstellung | null },
  wert: string | null,
): string {
  if (feld.art === 'jaNein' && wert) {
    return wert === 'true' ? t('ja') : wert === 'false' ? t('nein') : wert
  }

  if (feld.art === 'verweis' && wert) {
    return mitNamen(wert)
  }

  if (feld.art === 'periode') {
    return periodenwort(wert)
  }

  if (istZahlart(feld.art) && wert) {
    return zahlenwort(wert, feld.darstellung)
  }

  return wertwort(feld.feld, wert)
}

/**
 * Der Wert eines Auswahlfeldes als Wort.
 *
 * **Unbekanntes wird unverändert ausgegeben.** Ein Wertevorrat, den die API
 * kennt und die Sprachdatei noch nicht, ist sichtbar unbeschriftet — das ist
 * ehrlicher, als ihn zu verschweigen, und fällt beim Übersetzen auf.
 */
export function wertwort(feld: string, wert: string | null): string {
  if (!wert) {
    return '—'
  }

  return textOderRoh(
    `stammdaten.wert.${feld}.${wert}`,
    textOderRoh(`stammdaten.wert.${wert}`, wert),
  )
}

/**
 * Datum und Uhrzeit — für die Fassungsreihe (R517).
 *
 * **Das Datum allein reicht dort nicht mehr.** Solange die Reihe nur Auskunft
 * gab, genügte der Tag; seit man sie anklickt, stehen sechs Fassungen
 * desselben Nachmittags untereinander, und ein sechsmal gleicher Tag ist keine
 * Unterscheidung. Die Erfassungsnummer daneben trennt sie zwar — aber sie
 * sagt nicht, wann.
 */
function zeitstempel(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) {
    return iso
  }

  // **In der Sprache von CoKo, nicht der des Browsers** — wie jedes Datum
  // (siehe `tagesdatum`). **Mit Sekunde** (7.2 Abschnitt 36, R1141): Bis zum
  // 2026-10-06 trennte die Erfassungsnummer daneben, was in derselben Minute
  // entstand; ein Stand heißt jetzt nach seinem Zeitpunkt allein.
  return new Intl.DateTimeFormat(gebietsschema(), { dateStyle: 'short', timeStyle: 'medium' }).format(d)
}

/** Sucht über Code, Bezeichnung und Kürzel — die drei Dinge, die man kennt. */
function passend(eintraege: Listeneintrag[], suche: string): Listeneintrag[] {
  const wort = suche.trim().toLowerCase()
  if (!wort) {
    return eintraege
  }

  return eintraege.filter((e) =>
    [e.code, e.schluessel, ...Object.values(e.namen), ...Object.values(e.kuerzel ?? {})]
      .join(' ')
      .toLowerCase()
      .includes(wort),
  )
}

/**
 * Ein Stammdatum anlegen — Code und Name, sonst nichts (R315).
 *
 * **Die Eigenschaften kommen danach**, auf demselben Blatt wie bei jedem
 * anderen. Ein Anlegedialog, der alle Felder abfragt, wäre eine zweite Maske
 * für dieselbe Sache — und genau die soll es nicht geben.
 */
function Anlageblatt({
  objektartname,
  art,
  laeuft,
  vorbelegt,
  aufAnlegen,
  aufAbbruch,
}: {
  /**
   * Wie die Objektart am Bildschirm heisst — „Konten", nicht `konto`.
   *
   * **Der Name und nicht die Objektart** (Fund vom 2026-09-21 beim Bau): Das
   * Feld hiess `objektart` und trug den Anzeigenamen; wer damit eine Liste
   * holte, bekam `/api/stammdaten/Konten` und einen Befund. Was technisch
   * gemeint ist, steht in `art.objektart`.
   */
  objektartname: string
  art: Objektartbeschreibung | null
  laeuft: boolean
  /**
   * Felder, die schon feststehen, weil der Weg hierher sie gesetzt hat
   * (R713).
   *
   * **Bei den Organisationselementen sagt die Schaltfläche die Art:** Wer auf
   * *Gesellschaft aufnehmen* geklickt hat, hat die Frage „welche Art"
   * beantwortet, bevor die Maske aufging. Sie steht dann als Angabe da und
   * nicht als Auswahl — und erst damit weiß das Blatt, welchen Vorrat es für
   * das Objekt daneben holen muss.
   */
  vorbelegt?: Record<string, string>
  aufAnlegen: (code: string, name: string, felder: Record<string, string>) => void
  aufAbbruch: () => void
}) {
  /*
    **Das Codefeld steht einmal** (R327, berichtigt am 2026-09-15). Bei einer
    Beziehung — Beteiligung, Erstkonsolidierung, Kreiszugehörigkeit — ist der
    Code eines ihrer Pflichtfelder: Die Beteiligung heißt nach der Tochter.
    Bis dahin stand dieses Feld zweimal untereinander, als Code und als Feld,
    und beide sagten dasselbe. Der Dienst schreibt den Code ohnehin in das
    Codefeld; ein zweites Feld dafür beantwortet keine zweite Frage.
  */
  const felder = useMemo(
    () => (art?.felder ?? []).filter((f) => f.pflicht && f.feld !== art?.codefeld),
    [art],
  )

  /**
   * Wo der **Code selbst ein Verweis** ist (R327).
   *
   * Eine Kontozuordnung heißt nach ihrem Konto, eine Gliederungszeile nach
   * ihrer Position — der Code ist dort kein Name, den man sich ausdenkt,
   * sondern ein vorhandenes Objekt. Dann wird er ausgesucht: Wer hier
   * `KTO-9100` tippen müsste, könnte die Maske nur bedienen, wenn er die
   * Schlüssel auswendig kann.
   */
  const codeverweis = art?.felder.find((f) => f.feld === art.codefeld)?.verweisart ?? null

  /**
   * Ob diese Objektart **gar kein Codefeld mehr hat** (R676).
   *
   * Dann verlangt das Blatt keinen Code: Der Schlüssel wird gezogen, und was
   * der Anwender pflegt, sind Bezeichnung und Kurzbezeichnung (R677). Bei den
   * Beziehungen bleibt das Feld stehen — dort ist der Code ein Verweis, der
   * ausgesucht und nicht getippt wird.
   */
  const ohneCode = art !== null && art.codefeld === null

  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [werte, setWerte] = useState<Record<string, string>>(vorbelegt ?? {})

  /**
   * Welche Objektart eine Organisationsart meint (R712) — vom Dienst, nicht
   * von hier.
   *
   * Dass *Gesellschaft* die Objektart `einheit` meint, ist eine Aussage des
   * Modells; sie hier noch einmal hinzuschreiben hieße, sie an zwei Orten zu
   * pflegen (R240).
   */
  const brauchtZielarten = felder.some((f) => f.verweisartfeld)

  // Ein Nachschlagen im Hintergrund (R1007, R1211) — Ersatz: keine Zuordnung,
  // dann bietet das Feld keinen Vorrat an (R1212).
  const zielarten = useHintergrundabruf(
    brauchtZielarten ? () => api.organisationsarten() : null,
    [brauchtZielarten],
    KEINE_ZIELARTEN,
  )

  /**
   * Die gewählten Arten als ein Wort — die Kennung, an der das Vorratsladen
   * hängt.
   *
   * **Nicht `werte` selbst:** Dann lüde jede getippte Ziffer im Feld daneben
   * die Auswahllisten neu.
   */
  const artenstand = felder
    .filter((f) => f.verweisartfeld)
    .map((f) => werte[f.verweisartfeld!] ?? '')
    .join('|')

  /**
   * Worauf ein Verweisfeld zeigt — fest aus dem Katalog oder aus dem
   * Nachbarfeld (R713).
   */
  const zielart = (f: { verweisart: string | null; verweisartfeld?: string | null }): string | null =>
    f.verweisart ?? (f.verweisartfeld ? (zielarten[werte[f.verweisartfeld] ?? ''] ?? null) : null)

  /**
   * Wie die vorbelegte Art heißt — und damit zugleich, **ob** aufgenommen und
   * nicht angelegt wird (R713).
   */
  const aufnahmeart = (() => {
    const feld = felder.find((f) => f.verweisartfeld)?.verweisartfeld
    const wert = feld ? vorbelegt?.[feld] : undefined
    return feld && wert ? wertwort(feld, wert) : null
  })()

  // Die Auswahllisten der Verweisfelder: Eine Achse trägt man nicht als
  // Schlüssel ein, man sucht sie aus. Sie kommen aus derselben Liste, die
  // links steht — es gibt keinen zweiten Bestand.
  // Die Auswahllisten sind ein Nachschlagen im Hintergrund (R1007, R1211) —
  // Ersatz: keine Wahl (R1212).
  const vorrat = useHintergrundabruf(
    () => {
      const zuLaden: { schluessel: string; art: string }[] = [
        ...felder
          .map((f) => ({ schluessel: f.feld, art: zielart(f) }))
          .filter((z): z is { schluessel: string; art: string } => z.art !== null),
        ...(codeverweis ? [{ schluessel: 'code', art: codeverweis }] : []),
      ]

      return Promise.all(
        zuLaden.map(async (z) => {
          const eintraege = (await api.stammdatenliste(z.art)).eintraege
          // Ein Verweis auf eine Bewegungsart bietet keinen Vortrag an
          // (R1061) — was schon eingetragen ist, bleibt wählbar.
          return [
            z.schluessel,
            z.art === 'bewegungsart' ? ohneVortrag(eintraege, werte[z.schluessel]) : eintraege,
          ] as const
        }),
      ).then((paare) => Object.fromEntries(paare))
    },
    // `zielarten` und die gewählte Art gehören dazu: Erst mit ihnen steht
    // fest, welchen Vorrat ein Verweis mit wechselnder Zielart braucht (R713).
    [felder, codeverweis, zielarten, artenstand],
    KEIN_VORRAT,
  )

  /*
    **Die vorhandenen Elemente derselben Art** — für die Warnung unten (R678).
    Sie kommen aus derselben Liste, die links steht; ein zweiter Bestand wäre
    eine zweite Wahrheit.
  */
  // Nur für die Warnung, also im Hintergrund (R1007, R1211) — Ersatz: keine
  // Geschwister, dann bleibt die Warnung aus (R1212).
  const geschwister = useHintergrundabruf(
    !ohneCode || art?.beschriftbar === false
      ? null
      : () => api.stammdatenliste(art!.objektart).then((liste) => liste.eintraege),
    [art, ohneCode],
    KEINE_EINTRAEGE,
  )

  /*
    **Die Nummer darf doppelt sein — und das Blatt sagt es trotzdem** (R678).

    Seit die Nummer in der Bezeichnung steht, ist sie keine Kennung mehr: Zwei
    Konten dürfen beide mit „1400" anfangen. Ein Riegel machte sie durch die
    Hintertür wieder zu einer. Geblieben ist die Warnung — sie hält niemanden
    auf und lässt niemanden ins Messer laufen.

    **Gemessen wird das erste Wort**, denn das ist die Nummer. Steht dort
    nichts Nummernartiges, warnt auch nichts: „Sonstige Rückstellungen" und
    „Sonstige Erträge" sind keine Zwillinge.
  */
  const nummer = name.trim().split(/\s+/)[0] ?? ''
  const nummerAehnlich =
    ohneCode &&
    /^[0-9]/.test(nummer) &&
    name.trim() !== nummer &&
    geschwister.some((g) =>
      Object.values(g.namen).some((n) => n.trim().split(/\s+/)[0] === nummer),
    )

  /*
    **Die Ziffernprüfung ist entfallen** (R676, 2026-09-21). Sie stand hier für
    die Kontonummer und prüfte ein Feld, das es nicht mehr gibt: Die Nummer
    steht in der Bezeichnung und darf dort alles sein, was ein Haus als Nummer
    führt — „A. I. 2." so gut wie „1400". Was an ihre Stelle tritt, ist die
    Warnung oben: keine Absage, ein Hinweis.
  */
  /*
    **Eine ganze Zahl bleibt ganz** (R840). Die Nummer einer Dimension trägt
    keine Nachkommastellen; das Zahlenfeld liest „2,5" trotzdem als Zahl. Das
    Blatt sagt es dann unter dem Feld und legt nicht an, statt es erst vom
    Dienst abweisen zu lassen (R145). „2,00" dagegen ist 2: Die Speicherform
    führt keine Endnullen und geht ohne Nachkommastellen auf die Reise.
  */
  const keineGanze = (f: { feld: string; art: string }): boolean =>
    f.art === 'zahl' && (werte[f.feld] ?? '').includes('.')

  const vollstaendig =
    (ohneCode ? true : code.trim().length > 0) &&
    // **Ohne Code trägt die Bezeichnung die Identität** (R677): Ein Element
    // ohne jede Beschriftung wäre in keiner Liste wiederzufinden.
    (!ohneCode || art?.beschriftbar === false || name.trim().length > 0) &&
    felder.every((f) => (werte[f.feld] ?? '').trim().length > 0) &&
    !felder.some(keineGanze)

  function setze(feld: string, wert: string) {
    setWerte((alt) => ({ ...alt, [feld]: wert }))
  }

  return (
    <>
      {/*
        **Aufgenommen statt angelegt** (R713): Wo der Weg hierher die Art schon
        gesetzt hat, entsteht kein neues Objekt — ein vorhandenes wird in die
        Auswahl über dem Bericht geholt. „Neu" wäre hier eine falsche Ansage.
      */}
      <h2>
        {aufnahmeart
          ? t('stammdaten.aufnehmen', { art: aufnahmeart })
          : t('stammdaten.neu.titel', { objektart: objektartname })}
      </h2>

      <div className="stammdatenfelder">
        {/*
          **Kein Codefeld, wo es keinen Code gibt** (R676). Der Schlüssel wird
          gezogen; ihn zu zeigen, bevor er existiert, ginge nicht, und ein
          leeres Feld beantwortete keine Frage.
        */}
        {!ohneCode && (
        <label>
          <span className="name">
            {codeverweis
              ? t(`stammdaten.feld.${art?.codefeld}` as Sprachschluessel)
              : t('stammdaten.neu.code')}
          </span>

          {codeverweis ? (
            <Auswahlfeld
              wert={code}
              autoFocus
              leer="—"
              beschriftung={t(`stammdaten.feld.${art?.codefeld}` as Sprachschluessel)}
              eintraege={(vorrat.code ?? []).map(ausStammdatum)}
              aufWahl={setCode}
            />
          ) : (
            <input
              value={code}
              autoFocus
              onChange={(e) => setCode(e.target.value)}
            />
          )}

          <span className="erlaeuterung">
            {/*
              **Wo der Code eine Norm ist, sagt der Satz welche** (R681): Die
              Währung trägt ISO 4217, und „aus ihm entsteht der Schlüssel" wäre
              dort die zweitwichtigste Auskunft. `textOderRoh` fällt auf den
              allgemeinen Satz zurück, wo es keinen eigenen gibt.
            */}
            {codeverweis
              ? t('stammdaten.neu.codeverweis.erlaeuterung')
              : textOderRoh(
                  `stammdaten.neu.code.${art?.objektart}.erlaeuterung`,
                  t('stammdaten.neu.code.erlaeuterung'),
                )}
          </span>
        </label>
        )}

        {/*
          Kein Namensfeld, wo es keinen Namen gibt (R328): Eine Zuordnung heißt
          nach dem, was sie verbindet. Ein Feld dafür anzubieten hieße, den
          Anwender etwas eintragen zu lassen, das hinterher abgewiesen wird.
        */}
        {art?.beschriftbar !== false && (
          <label>
            <span className="name">{t('stammdaten.neu.name')}</span>
            <input
              value={name}
              autoFocus={ohneCode}
              aria-describedby={nummerAehnlich ? 'stammdaten-nummer-hilfe' : undefined}
              onChange={(e) => setName(e.target.value)}
            />

            {/*
              **Kein Ratschlag ab Werk unter dem Namensfeld** (Befund des
              Auftraggebers vom 2026-09-25). Der Satz über die vorangestellte
              Nummer stand hier für JEDE Objektart ohne Codefeld — also
              ausgerechnet dort, wo er nicht hingehört: Beim Konto, wo er
              stimmte, erscheint er nie, denn ein Konto hat eine Kennung.
              Wer wirklich etwas zur Bezeichnung zu sagen hat, sagt es je
              Objektart, wie es die Kennung daneben schon tut (`textOderRoh`).
            */}
            {nummerAehnlich && (
              <span className="hinweis" id="stammdaten-nummer-hilfe">
                {t('stammdaten.neu.nummer.schonda', { nummer })}
              </span>
            )}
          </label>
        )}

        {/*
          Die Pflichtfelder stehen hier und nicht auf dem Blatt danach (R318):
          Ohne sie gibt es das Objekt nicht — ein Dimensionswert ohne Achse ist
          keine halbe Kostenstelle, sondern keine. Alles Weitere wird danach
          gepflegt, dort, wo es auch geändert wird.
        */}
        {/*
          **Eine Periode wird im Periodenfeld getippt** (R839 — Eine Periode
          liest sich als Monat und Jahr; 7.2 Abschnitt 21.1).

          **Der Befund vom 2026-09-29:** Hier stand ein freies Feld mit dem
          Platzhalter „MM.JJJJ". Wer „09.2026" tippte, verlor die Periode
          still — das Formular schickte den Text, und der Server las nur
          Zahlen. Wer „202609" tippte, bekam das Feld beim Verlassen geleert.
          Jetzt steht hier dasselbe Feld wie überall: Es nimmt beide
          Schreibweisen, und auf die Reise geht die Speicherform.

          **Ohne zweiten Rahmen** (`klasse=""`): Das Raster setzt seine Felder
          über `> label`, und das Periodenfeld bringt sein `label` mit.
        */}
        {felder.map((f) =>
          f.art === 'periode' && vorbelegt?.[f.feld] === undefined ? (
            <Periodenfeld
              key={f.feld}
              klasse=""
              beschriftung={t(`stammdaten.feld.${f.feld}` as Sprachschluessel)}
              wert={werte[f.feld] ? Number(werte[f.feld]) : null}
              aufWert={(periode) => setze(f.feld, periode === null ? '' : String(periode))}
            />
          ) : istZahlart(f.art) && vorbelegt?.[f.feld] === undefined ? (
            /*
              **Eine Zahl wird im Zahlenfeld getippt** (R840 — Eine Zahl in den
              Stammdaten steht wie im Bericht; 7.2 Abschnitt 22.1).

              **Der Befund vom 2026-09-29:** Hier stand das Zahlenfeld des
              Browsers. „80 %" beim Kapitalanteil nahm es nicht an,
              „1.600.000,00" bei den Anschaffungskosten auch nicht, und der
              Anteil war als Bruch zu tippen. Jetzt steht hier dasselbe Feld
              wie auf dem Blatt und in der Tafel: Ein Anteil wird in Prozent
              getippt, ein Betrag in der Schreibweise des Anwenders, und auf
              die Reise geht die Speicherform.

              **Ohne zweiten Rahmen** (`klasse=""`), wie die Periode darüber.
            */
            <Zahlenfeld
              key={f.feld}
              klasse=""
              beschriftung={t(`stammdaten.feld.${f.feld}` as Sprachschluessel)}
              darstellung={f.darstellung ?? null}
              wert={werte[f.feld] || null}
              aufWert={(zahl) => setze(f.feld, zahl ?? '')}
              erlaeuterung={keineGanze(f) ? t('stammdaten.zahl.nurganz') : undefined}
            />
          ) : (
          <label key={f.feld}>
            <span className="name">{feldname(art?.objektart, f.feld)}</span>

            {/*
              **Was der Weg hierher schon entschieden hat, steht als Angabe da**
              (R713): Wer auf „Gesellschaft aufnehmen" geklickt hat, soll die
              Art nicht noch einmal wählen — eine Auswahl mit genau einer
              richtigen Antwort ist keine.
            */}
            {vorbelegt?.[f.feld] !== undefined ? (
              <span className="wert">
                {/* Periode als Monat und Jahr (R839), Zahl wie im Bericht (R840). */}
                {wertanzeige(f, vorbelegt[f.feld] ?? null)}
              </span>
            ) : zielart(f) ? (
              <Auswahlfeld
                wert={werte[f.feld] ?? ''}
                leer="—"
                beschriftung={t(`stammdaten.feld.${f.feld}` as Sprachschluessel)}
                eintraege={(vorrat[f.feld] ?? []).map(ausStammdatum)}
                aufWahl={(gewaehlt) => setze(f.feld, gewaehlt)}
              />
            ) : f.art === 'auswahl' && f.vorrat ? (
              <Auswahlfeld
                sorte="aufzaehlung"
                wert={werte[f.feld] ?? ''}
                leer="—"
                beschriftung={t(`stammdaten.feld.${f.feld}` as Sprachschluessel)}
                eintraege={f.vorrat.map((v) => ({ wert: v, text: wertwort(f.feld, v), suchtext: v }))}
                aufWahl={(gewaehlt) => setze(f.feld, gewaehlt)}
              />
            ) : (
              <input
                value={werte[f.feld] ?? ''}
                onChange={(e) => setze(f.feld, e.target.value)}
              />
            )}
          </label>
          ),
        )}
      </div>

      <p className="handreichung">
        <button
          type="button"
          className="haupt"
          disabled={laeuft || !vollstaendig}
          onClick={() => aufAnlegen(code.trim(), name.trim(), werte)}
        >
          {t(aufnahmeart ? 'stammdaten.aufnehmen.speichern' : 'stammdaten.neu.speichern')}
        </button>{' '}
        <button type="button" onClick={aufAbbruch}>
          {t('import.schliessen')}
        </button>
      </p>
    </>
  )
}

/**
 * Ein **Abschnitt des Blattes** — aufklappbar, und der Zustand wird gemerkt.
 *
 * **Dieselbe Überschrift, dieselbe Unterlinie, derselbe Abstand** wie vorher
 * (`h2.abschnitt`); dazu ein Aufklappen, das CoKo an vielen Stellen schon
 * führt — im Journal, im Belegordner, in den Kacheln. Es ist `<details>` und
 * kein nachgebauter Knopf: Der Browser bringt Tastaturbedienung,
 * `aria-expanded` und das Suchen im Text mit, und drei davon vergisst man beim
 * Nachbauen.
 *
 * **Der Zustand wird je Anwender gemerkt** (L2, wie der Aufklappzustand des
 * Positionsbaums und die Seitenleiste): Wer die Eigenschaften dauernd braucht
 * und die Beschriftungen nie, richtet sich das einmal ein.
 *
 * **Die Zusammenfassung trägt eine Auskunft**, wo es eine gibt — *3 Sprachen*,
 * *16 Eigenschaften*. Zugeklappt ist eine Überschrift ohne Inhalt eine Zeile,
 * die man aufklappen muss, um zu wissen, ob sich das lohnt.
 */
export function Blattabschnitt({
  kennung,
  titel,
  zusatz,
  vorgabe = true,
  auf = false,
  children,
}: {
  /** Worunter der Aufklappzustand gemerkt wird. */
  kennung: string
  titel: string
  /** Was zugeklappt von dem Abschnitt übrig bleibt — eine Zahl, ein Wort. */
  zusatz?: string
  vorgabe?: boolean
  /**
   * Klappt auf, sobald es `true` wird — etwa, wenn eine Suche darin trifft
   * (5.1 Abschnitt 3.49, R1151). Gemerkt wird das nicht: Es ist ein Anlass,
   * keine Entscheidung des Anwenders.
   */
  auf?: boolean
  children: React.ReactNode
}) {
  const [offen, setOffen] = useState(() => gemerkt(kennung, vorgabe))

  useEffect(() => {
    if (auf) {
      setOffen(true)
    }
  }, [auf])

  return (
    <details
      className="blattabschnitt"
      open={offen}
      onToggle={(e) => {
        const jetzt = (e.currentTarget as HTMLDetailsElement).open
        if (jetzt === offen) {
          return
        }
        setOffen(jetzt)
        merke(kennung, jetzt)
      }}
    >
      <summary>
        <h2 className="abschnitt">{titel}</h2>
        {zusatz && <span className="zusatz">{zusatz}</span>}
      </summary>

      <div className="abschnittsinhalt">{children}</div>
    </details>
  )
}

const ABLAGE = 'coko.blattabschnitt.'

/**
 * Der Klappzustand der Fassungsspalte — **im selben Fach wie die Abschnitte
 * des Blattes**, weil es dieselbe Sorte Entscheidung ist: was der Anwender
 * offen haben will.
 *
 * **Die Vorgabe ist zu.** Wer noch nie geklickt hat, bekommt das Blatt in
 * voller Breite; die Geschichte holt er sich, wenn er sie braucht.
 */
function fassungsspalteGemerkt(): boolean {
  return !gemerkt(FASSUNGSSPALTE, false)
}

function merkeFassungsspalte(zu: boolean): void {
  merke(FASSUNGSSPALTE, !zu)
}

const FASSUNGSSPALTE = 'fassungsspalte'

/**
 * Was der Anwender zuletzt offen hatte.
 *
 * **Ein gesperrter Speicher ist kein Fehler**, über den jemand etwas erfahren
 * müsste — dann gilt die Vorgabe (dieselbe Haltung wie bei der Sprache).
 */
function gemerkt(kennung: string, vorgabe: boolean): boolean {
  try {
    const wert = window.localStorage.getItem(ABLAGE + kennung)
    return wert === null ? vorgabe : wert === 'auf'
  } catch {
    return vorgabe
  }
}

function merke(kennung: string, offen: boolean): void {
  try {
    window.localStorage.setItem(ABLAGE + kennung, offen ? 'auf' : 'zu')
  } catch {
    /* siehe oben */
  }
}

/**
 * Löschen — **ein Fenster über dem Blatt** (R509–R512, R710).
 *
 * **Bis zum 2026-09-24 war es der vierte Abschnitt des Blattes.** Das war
 * richtig gedacht und im Gebrauch falsch: Der Abschnitt stand unter den
 * Eigenschaften, klappte zu, und wer löschen wollte, scrollte erst an
 * sechzehn Feldern vorbei, die mit dem Löschen nichts zu tun haben. Was das
 * Löschen braucht, steht jetzt in einem Fenster, und der Weg dorthin ist ein
 * Knopf oben am Blatt — dort, wo die Überschrift sagt, worum es geht.
 *
 * **Im Fenster gilt die Sprache des Fensters** (R515): Kopf mit Kreuz, Fuß
 * mit der Knopfreihe, dazwischen die Maske. Die Knöpfe stehen deshalb hier
 * und nicht mehr am Ende der Maske — in einem Fenster, das man kleiner ziehen
 * kann, wäre ein mitlaufender Knopf genau dann außer Sicht, wenn man ihn
 * sucht.
 *
 * **Die Fundstellen klappen auf** (R515): zugeklappt eine Zeile je Art mit
 * ihrer Zahl, aufgeklappt die Einzelheiten samt Belegverweisen. Vor dem
 * Löschen zählt die Zahl; die Belege sieht an, wer zweifelt.
 *
 * **Drei Arten von Fund, drei Bedeutungen** (R509): Bewegungsdaten stehen auf
 * dem Element, Kinder sind eigene Objekte, die es weiter gibt, eigene
 * Zuordnungen gehen mit. **Gesperrt wird nur durch einen freigegebenen
 * Abschluss** (R512).
 */
export function Loeschfenster({
  lage,
  code,
  vorrat,
  laeuft,
  aufLoeschen,
  aufSchliessen,
}: {
  /** Was der Dienst gefunden hat — `null`, solange er noch nachsieht. */
  lage: Loeschlage | null
  code: string
  /** Die möglichen Ziele für die Kinder — dieselbe Objektart (R327). */
  vorrat: Listeneintrag[]
  laeuft: boolean
  aufLoeschen: (auftrag: Loeschauftrag) => void
  aufSchliessen: () => void
}) {
  const [periode, setPeriode] = useState<number | null>(() => {
    const jetzt = new Date()
    return jetzt.getFullYear() * 100 + jetzt.getMonth() + 1
  })
  const [weg, setWeg] = useState<'stilllegen' | 'entfernen'>('stilllegen')
  const [ziel, setZiel] = useState('')
  const [verzichtet, setVerzichtet] = useState(false)

  /*
    **Jedes Element fängt bei null an** (R510). Das besorgt seit dem Umzug ins
    Fenster der Bau selbst: Das Fenster entsteht mit dem Klick und vergeht mit
    dem Schließen, und damit auch Ziel, Weg und vor allem der **Verzicht**.
    Ein Verzicht, den jemand für ein anderes Element ausgesprochen hat, ist
    keiner: Er wird ausgesprochen, nicht geerbt. Solange die Maske am Blatt
    hing, brauchte es dafür einen eigenen Haken.
  */

  const hatKinder = (lage?.kinder.length ?? 0) > 0
  const nichtsGefunden =
    lage !== null &&
    lage.bewegung.length === 0 &&
    lage.kinder.length === 0 &&
    lage.eigen.length === 0

  // **Der einfache Fall bleibt einfach** (R510): Wo nichts steht und nichts
  // hängt, gibt es keine Wahl zu treffen — nur den Knopf, der sagt, was er tut.
  const einfach = nichtsGefunden || (lage !== null && lage.bewegung.length === 0 && !hatKinder)

  const gewaehlterWeg = einfach ? 'entfernen' : weg
  const bereit =
    lage !== null &&
    (!hatKinder || ziel !== '') &&
    (gewaehlterWeg === 'stilllegen' ? periode !== null : einfach || verzichtet)

  return (
    <Dialogfenster
      titel={t('loeschen.stammdatum.titel', { code })}
      weite="mittel"
      aufSchliessen={aufSchliessen}
      fuss={
        <>
          <button
            type="button"
            className="gefahr"
            disabled={laeuft || !bereit}
            onClick={() =>
              aufLoeschen({
                weg: gewaehlterWeg,
                abPeriode: gewaehlterWeg === 'stilllegen' ? periode : null,
                ziel: hatKinder ? ziel : null,
              })
            }
          >
            {gewaehlterWeg === 'entfernen'
              ? t('loeschen.tun.entfernen')
              : t('loeschen.tun.stilllegen', { periode: periodenwort(periode) })}
          </button>
          <button type="button" onClick={aufSchliessen}>
            {t('loeschen.zurueck')}
          </button>
        </>
      }
    >
      {lage === null ? (
        <p className="hinweis">{t('loeschen.wirdgeprueft')}</p>
      ) : (
        <Loeschwahl
          lage={lage}
          code={code}
          vorrat={vorrat}
          laeuft={laeuft}
          einfach={einfach}
          nichtsGefunden={nichtsGefunden}
          hatKinder={hatKinder}
          periode={periode}
          weg={weg}
          ziel={ziel}
          verzichtet={verzichtet}
          aufPeriode={setPeriode}
          aufWeg={setWeg}
          aufZiel={setZiel}
          aufVerzicht={setVerzichtet}
        />
      )}
    </Dialogfenster>
  )
}

/**
 * Was gefunden wurde und welche Wege offenstehen — der Inhalt des Fensters.
 *
 * **Sie zeigt und nimmt entgegen, sie entscheidet nichts.** Ob gelöscht
 * werden kann und was der Knopf dann heißt, rechnet das Fenster aus — dort
 * steht auch der Knopf (R710).
 */
function Loeschwahl({
  lage,
  code,
  vorrat,
  laeuft,
  einfach,
  nichtsGefunden,
  hatKinder,
  periode,
  weg,
  ziel,
  verzichtet,
  aufPeriode,
  aufWeg,
  aufZiel,
  aufVerzicht,
}: {
  lage: Loeschlage
  code: string
  vorrat: Listeneintrag[]
  laeuft: boolean
  /** Ob es nichts zu wählen gibt — dann fällt die Wegauswahl weg (R510). */
  einfach: boolean
  nichtsGefunden: boolean
  hatKinder: boolean
  periode: number | null
  weg: 'stilllegen' | 'entfernen'
  ziel: string
  verzichtet: boolean
  aufPeriode: (p: number | null) => void
  aufWeg: (w: 'stilllegen' | 'entfernen') => void
  aufZiel: (z: string) => void
  aufVerzicht: (v: boolean) => void
}) {
  return (
    <>
      <p className="erlaeuterung">
        {nichtsGefunden
          ? t('loeschen.nichtsgefunden')
          : t('loeschen.gefunden.erlaeuterung', { code })}
      </p>

      {/*
        **Zugeklappt die Zahl, aufgeklappt die Einzelheiten** (R515). Drei
        Arten, drei Zeilen — und der Unterschied zwischen ihnen steht in der
        Zeile selbst, nicht in einer Legende daneben.
      */}
      {lage.bewegung.length > 0 && (
        <Fundstelle
          titel={t('loeschen.gefunden')}
          stellen={lage.bewegung}
          erlaeuterung={t('loeschen.bewegung.text')}
        />
      )}

      {lage.eigen.length > 0 && (
        <Fundstelle
          titel={t('loeschen.eigen.titel')}
          stellen={lage.eigen}
          erlaeuterung={t('loeschen.eigen.text')}
        />
      )}

      {hatKinder && (
        <Fundstelle
          titel={t('loeschen.kinder.titel')}
          stellen={lage.kinder}
          erlaeuterung={t('loeschen.kinder.text')}
          offen
        />
      )}

      {/*
        Das Raster des Blattes, nicht ein eigenes: Beschriftung links, Feld
        rechts — wie die Eigenschaften darüber (R515).
      */}
      <div className="stammdatenfelder">
        {hatKinder && (
          <label>
            <span className="name">{t('loeschen.ziel')}</span>
            <Auswahlfeld
              wert={ziel}
              abgeschaltet={laeuft}
              leer="—"
              beschriftung={t('loeschen.ziel')}
              eintraege={vorrat
                .filter((e) => e.schluessel !== lage.schluessel)
                .map(ausStammdatum)}
              aufWahl={aufZiel}
            />
            <span className="erlaeuterung">{t('loeschen.ziel.erlaeuterung')}</span>
          </label>
        )}
      </div>

      {!einfach && (
        <div className="loeschwege">
          <span className="beschriftung">{t('loeschen.weg')}</span>

          <label>
            <input
              type="radio"
              name="loeschweg"
              checked={weg === 'stilllegen'}
              onChange={() => aufWeg('stilllegen')}
            />
            <span className="name">{t('loeschen.weg.stilllegen')}</span>
            <span className="erlaeuterung">{t('loeschen.weg.stilllegen.text')}</span>
          </label>

          {weg === 'stilllegen' && (
            <div className="wegfeld stammdatenfelder">
              <label>
                <span className="name">{t('stammdaten.abperiode')}</span>
                <Periodenfeld
                  wert={periode}
                  aufWert={aufPeriode}
                  beschriftung={t('stammdaten.abperiode')}
                  klasse="nurfeld"
                  gesperrt={laeuft}
                />
              </label>
            </div>
          )}

          <label>
            <input
              type="radio"
              name="loeschweg"
              disabled={!lage.entfernenMoeglich}
              checked={weg === 'entfernen'}
              onChange={() => aufWeg('entfernen')}
            />
            <span className="name">{t('loeschen.weg.entfernen')}</span>
            <span className="erlaeuterung">{t('loeschen.weg.entfernen.text')}</span>
          </label>

          {/*
            **Der Verzicht wird ausgesprochen, nicht angenommen** (R510): Wer
            die Vergangenheit aufgibt, soll den Satz gelesen haben, in dem das
            steht.
          */}
          {weg === 'entfernen' && lage.entfernenMoeglich && (
            <label className="verzicht">
              <input
                type="checkbox"
                checked={verzichtet}
                onChange={(e) => aufVerzicht(e.target.checked)}
              />
              <span className="name">{t('loeschen.verzicht')}</span>
            </label>
          )}

          {!lage.entfernenMoeglich && (
            <p className="hinweis">{t('loeschen.gesperrt', { anzahl: anzahlwort(lage.sperren.length) })}</p>
          )}
        </div>
      )}
    </>
  )
}

/**
 * Eine Art von Fund — **zugeklappt die Zahl, aufgeklappt die Einzelheiten**
 * (R515).
 *
 * **Die Kinder stehen offen**, die übrigen zu: An ihnen hängt eine
 * Entscheidung (wohin sie gehören), an den anderen nur eine Auskunft.
 */
function Fundstelle({
  titel,
  stellen,
  erlaeuterung,
  offen = false,
}: {
  titel: string
  stellen: Verwendung[]
  erlaeuterung: string
  offen?: boolean
}) {
  const summe = stellen.reduce((s, v) => s + v.anzahl, 0)

  return (
    <details className="fundstelle" open={offen}>
      <summary>
        <span className="name">{titel}</span>
        <span className="zahl">{summe.toLocaleString(zahlenschema())}</span>
      </summary>

      <div className="fundstelleninhalt">
        <p className="erlaeuterung">{erlaeuterung}</p>
        <Stellenliste stellen={stellen} />
      </div>
    </details>
  )
}

/**
 * Die Fundstellen mit ihrer Zahl — und den Belegen, solange es wenige sind
 * (R511).
 *
 * **Eine Zahl sagt, wie viel; ein Verweis sagt, was.** Vor der Frage „kann ich
 * das löschen" will ein Anwender bei drei Belegen nachsehen und bei tausend
 * nicht.
 */
function Stellenliste({ stellen }: { stellen: Verwendung[] }) {
  return (
    <ul className="verwendungsliste">
      {stellen.map((v) => (
        <li key={v.stelle}>
          <span className="code">{textOderRoh(`stammdaten.stelle.${v.stelle}`, v.stelle)}</span>
          <span className="zahl">{anzahlwort(v.anzahl)}</span>

          {(v.belege?.length ?? 0) > 0 && (
            <ul className="belegverweise">
              {v.belege!.map((b) => (
                <li key={b.id}>
                  <a href={`#/nachweis/buchungsjournal?beleg=${b.id}`}>
                    {b.nummer} · {periodenname(b.periode)}
                    {b.einheit ? ' · ' + gesellschaftsname(b.einheit) : ''}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ul>
  )
}

/**
 * Der Wert, nach dem sortiert wird — Code, Name oder ein gruppierbares Feld.
 *
 * **Leer kommt ans Ende**, nicht an den Anfang: Eine Liste, die mit den
 * ungepflegten Zeilen beginnt, sieht aus, als sei sie leer.
 */
function wertVon(e: Listeneintrag, feld: string): string {
  if (feld === 'code') return e.code
  if (feld === 'name') return beschriftung(e.namen) || e.code
  const wert = e.werte[feld] ?? ''
  return wert === '' ? '￿' : wert
}

/**
 * Die Sicht eines Anwenders auf eine Objektart — **im Browser gemerkt, nirgends
 * gespeichert** (R557).
 *
 * **Sie geht niemanden sonst etwas an.** Wonach jemand seine Liste gliedert,
 * ist keine Aussage über die Daten und hat deshalb in der Datenbank nichts zu
 * suchen; ein Kollege am Nebentisch soll seine eigene Ordnung sehen.
 *
 * Der Browser gibt den Speicher nicht immer her — privates Fenster, gesperrte
 * Seitendaten. Dann gilt die Vorgabe, und zwar still: Eine Meldung darüber,
 * dass eine Sortierung nicht gemerkt werden konnte, hilft niemandem.
 */
/**
 * Wie breit die linke Spalte anfängt (Auftrag vom 2026-09-27).
 *
 * **Breiter als die 300 Bildpunkte von vorher**: In ihnen blieb von *„100
 * Zugang zu Anschaffungskosten"* ein *„100 Zugang zu Ansc…"* übrig, und das
 * betraf die Mehrzahl der Bewegungsarten.
 */
const SPALTENVORGABE = 420

/**
 * Die Breite gilt für den ganzen Bildschirm und nicht je Objektart — anders
 * als Gruppierung und Sortierung, die zu dem gehören, was man ansieht.
 */
const SICHTWEIT = 'ansicht'

function gemerkteSicht(objektart: string, was: string, vorgabe: string): string {
  try {
    return window.localStorage.getItem(`coko.stammdaten.${objektart}.${was}`) ?? vorgabe
  } catch {
    return vorgabe
  }
}

function merkeSicht(objektart: string, was: string, wert: string): void {
  try {
    window.localStorage.setItem(`coko.stammdaten.${objektart}.${was}`, wert)
  } catch {
    // Siehe oben: still.
  }
}
